/**
 * Mock twin of the cost-engine validation surfaces (product brief
 * `docs/features/cost-engine-productionisation/01_product_brief.md` §8,
 * review decisions in `02_glossary_and_live_costing.md`) — the dictionary
 * admin pages' "how confident am I in costing" lens: per-item cost-check/
 * live-costing state, the cost-centre × component cost-line board, the live
 * costing checks, the check-classified variant matrix, the rolling
 * validation set (priced at item × variant × quantity — never coarser), and
 * the engine-vs-Tharstern cost pair with the presses the route enumeration
 * considered.
 *
 * Fixtures are realistic, not exhaustive: six dictionary items span the four
 * cost-check states plus a 40+-variant item (to
 * exercise the variant-matrix's paging), eight cost lines span
 * green/amber/red/grey, and the numbers are chosen to be internally
 * consistent (a mismatching item's matrix carries cells tagged with its
 * mismatching cost line; the cost-line board's check/miss counts roughly
 * match the items citing it).
 *
 * Tharstern's own names for cost centres, op codes, cost heads and machines
 * throughout (naming.md — preserve the source domain's terms).
 */

import type { ListResponse } from "../data/adapter";
import type { LiveCostingEstateDetail } from "../data/manufacturing-setup-types";
import type {
  CheckSettings,
  CheckSettingsUpdateRequest,
  CostCentreValidationIndexEntry,
  CostCheckHistoryEntry,
  CostCheckState,
  CostHead,
  CostLineAffectedItem,
  CostLineCheck,
  CostLineIndexEntry,
  CostLineMoneySummary,
  CostLineRef,
  CostPairGroupReason,
  CostPairLine,
  DictionaryValidationFeedsStatus,
  DictionaryValidationIndexEntry,
  DictionaryValidationListOptions,
  DictionaryValidationMatrix,
  DictionaryValidationMatrixOptions,
  EngineValidationCostPair,
  LiveCostingChecklistItem,
  LiveCostingDetail,
  CostCheckCellState,
  CheckSubstitution,
  PressConsidered,
  ProductTypeValidationIndexEntry,
  QuarantinedPrice,
  RollingSetOrigin,
  RouteReasonCode,
  SpecificationField,
  ValidationMatrixCell,
  ValidationMatrixRow,
  FixedCostOptionChip,
  SupersedeChecksRequest,
  SupersedeChecksResult,
  SupersedeEvent,
  SupersedePreview,
} from "../data/cost-engine-validation-types";
import {
  formatRouteReasonText,
  productTypeLabel,
  type RouteReasonParams,
  fixedCostOptionsKey,
} from "../domain/cost-engine-validation-presentation";
import { fixedCostOptionSearchValues } from "../routing/routes";
import {
  getMockCostCentreRef,
  isOriginationProcessType,
} from "./manufacturing-setup";

const CHECK_WINDOW_WEEKS = 4;
// The routing policy's live version — kept in step with `routing-policy.ts`'s
// own `HISTORY` (version 3) so the pair page's "policy v3" link and the
// Routing policy page never disagree.
const ROUTING_POLICY_VERSION = 3;

/** Builds a group-header "why", pre-rendered from a closed `RouteReasonCode`
 *  — the mock never authors free-text whys independent of a code (build
 *  note, `04_handover_2026-09-12.md`). */
function costCentreReason(
  code: RouteReasonCode,
  params: RouteReasonParams = {},
  partSpecLabel?: string,
): CostPairGroupReason {
  return {
    code,
    text: formatRouteReasonText(code, params),
    policyVersion: ROUTING_POLICY_VERSION,
    partSpecLabel,
  };
}

/** "Costed 10 Sep 04:12" — the single v1 timestamp every price shows (no
 *  engine-version/standards-as-of pair). Exported so other mock-data
 *  modules (catalogue.ts's price preview) share one constant. */
export const COSTED_AT = isoDaysAgo(2, 3, 0);

/** The rolling-validation timer's next hourly slice (`*:17` UTC) after
 *  now, and the one before it — the feed runs hourly since 24 Sep 2026. */
function rollingSliceAt(hoursFromNext: number): string {
  const d = new Date();
  d.setUTCMinutes(17, 0, 0);
  if (d.getTime() <= Date.now()) d.setUTCHours(d.getUTCHours() + 1);
  d.setUTCHours(d.getUTCHours() + hoursFromNext);
  return d.toISOString();
}

function isoDaysAgo(days: number, hour = 9, minute = 0): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - days);
  d.setUTCHours(hour, minute, 0, 0);
  return d.toISOString();
}

// ── Cost line registry ──────────────────────────────────────────────────────
// Tharstern's own cost-centre ids/codes/names (naming.md). Keyed by
// `${costCentreId}:${component}` so a matrix cell or a check can cite one
// exactly. costHead is Tharstern's own cost-head classification, used to
// group lines on the cost-pair page.

/** Cost-centre code — the pair line's `costCentreCode` field — looked up
 *  from manufacturing-setup.ts rather than retyped, so a hand-authored
 *  `CostPairLine` can never carry a code the Setup page disagrees with. */
function ccCode(costCentreId: number): string {
  return getMockCostCentreRef(costCentreId).code;
}

function ccName(costCentreId: number): string {
  return getMockCostCentreRef(costCentreId).name;
}

/** "5351 5COLXL75LEUV" / "5-Colour XL75 LE UV" — the two `PressConsidered`
 *  fields, sourced the same way. */
function pressRef(costCentreId: number): {
  pressCode: string;
  pressName: string;
} {
  const { code, name } = getMockCostCentreRef(costCentreId);
  return { pressCode: `${costCentreId} ${code}`, pressName: name };
}

/** The cost-pair page's URL for one (item, variant, quantity) price — a
 *  check's, a matrix cell's, and the pair itself's `_self` (spec 05 §9.1's
 *  self-link table; `views_module.api.transformers.self_links.
 *  cost_check_pair_self` on the real backend). */
function costCheckPairSelf(
  dictionaryItemId: string,
  variantId: string,
  quantity: number,
  fixedCostOptions: readonly FixedCostOptionChip[] = [],
): string {
  const path = `/views/live-costing/pairs/${dictionaryItemId}/${variantId}/${quantity}`;
  const values = fixedCostOptionSearchValues(fixedCostOptions);
  return values.length === 0
    ? path
    : `${path}?${new URLSearchParams(values.map((v) => ["fixed_cost_option", v])).toString()}`;
}

function costLine(
  costCentreId: number,
  costHead: CostHead,
  component: string,
): CostLineRef {
  // Code and name always come from manufacturing-setup.ts's cost-centre
  // snapshot — the single source (naming.md: one name per concept, in
  // every layer) — never a second hand-typed copy that can drift from what
  // the Setup and Routing policy pages show for the same cost centre.
  const { code, name } = getMockCostCentreRef(costCentreId);
  return {
    costCentreId,
    costCentreCode: code,
    costCentreName: name,
    costHead,
    component,
    // Includes the code, not just the id — "5351" alone means nothing to
    // Henry; "5351 5COLXL75LEUV" is the same cost centre the Equipment and
    // Setup pages already identify this way (coordinator review round 6).
    label: `${costCentreId} ${code} · ${component}`,
  };
}

/** The cost-centre-less cost line. Whole cost heads carry no cost centre —
 *  our paper, outwork and order-expense lines have none, and Tharstern's
 *  equivalent lines carry none either — and on staging paper is the single
 *  biggest line on the board. The public id uses `0`
 *  (`views_module.actions._cost_line_id.NO_COST_CENTRE_ID`), and there is no
 *  cost-centre page to link to. */
const COST_LINE_PAPER: CostLineRef = {
  costCentreId: 0,
  costCentreCode: "",
  costCentreName: "",
  costHead: "paper",
  component: "Paper",
  label: "No cost centre · Paper",
};

const COST_LINE_5351_INK = costLine(5351, "other_material", "Ink");
// Same cost centre as the ink line above, different component — 5351 also
// runs the brochure cover's litho print, and that line matches exactly
// (only the ink material line mismatches). The by-cost-centre view groups
// both under one 5351 row (coordinator review round 6).
const COST_LINE_5351_PRINTRUN = costLine(5351, "printing", "Print run");
const COST_LINE_803_PRINT = costLine(803, "printing", "Print run");
const COST_LINE_5830_PRINT = costLine(5830, "printing", "Print run");
const COST_LINE_1117_PRINT = costLine(1117, "printing", "Print run");
const COST_LINE_5627_PRINT = costLine(5627, "printing", "Print run");
const COST_LINE_801_FINISHING = costLine(801, "finishing", "Finishing run");
const COST_LINE_835_FINISHING = costLine(835, "finishing", "Finishing run");
// Codes/names/sites match manufacturing-setup.ts's cost centre snapshot
// exactly (5607 HYBRID SHEET at Buckingham, 5609 HYBRID REEL at Buckingham
// — both candidates on product type 44's printing step) so the Setup
// mirror, the step map, and this pair page never disagree about which
// presses exist or where they sit (Read it as Henry: mock data internally
// consistent across pages).
const COST_LINE_5607_RUN = costLine(5607, "printing", "Run");
const COST_LINE_5609_RUN = costLine(5609, "printing", "Run");

/** Business cards' 400gsm uncoated: Tharstern's echo comes back on a
 *  different paper code from the one we send. The Mappings tab shows the
 *  same substitution on that option's Tharstern leaf. */
export const BC_2_SUBSTITUTION: CheckSubstitution = {
  field: "paper",
  label: "Paper",
  sent: "WWW_UNC_400",
  used: "WWW_UNC_400_ALT",
};

interface CostLineFixture {
  costLine: CostLineRef;
  health: CostCheckState;
  productTypesAffected: number[];
  totalCheckCount: number;
  missCount: number;
  /** Summed over the cost line's checks. `null` for a never-checked cost
   *  line — there is nothing to sum, not a sum of zero. */
  ourMoneyPence: number | null;
  tharsternMoneyPence: number | null;
  lastCheckedAt: string | null;
  standardsRows: string[];
  /** Check generator seed — plausible dictionary/variant refs to cite. */
  checkSeeds: Array<{
    dictionaryItemId: string;
    dictionaryItemName: string;
    variantId: string;
    variantLabel: string;
    productTypeId: number;
  }>;
  /**
   * Explicit per-miss Δ values (pence, ours − Tharstern), in generation
   * order. Only set where the board's worked example needs a realistic mix
   * of over/under checks rather than one uniform delta; length must equal
   * `missCount`. Falls back to an even split of the aggregate delta across
   * `missCount` checks when omitted.
   */
  missDeltasPence?: number[];
}

const COST_LINE_FIXTURES: CostLineFixture[] = [
  {
    costLine: COST_LINE_PAPER,
    health: "amber",
    productTypesAffected: [1],
    totalCheckCount: 18,
    missCount: 3,
    ourMoneyPence: 54000,
    tharsternMoneyPence: 54600,
    lastCheckedAt: isoDaysAgo(1),
    standardsRows: ["Sheet size × sheets × stock price, from the paper table"],
    checkSeeds: [
      {
        dictionaryItemId: "dict-brochure-cover-text",
        dictionaryItemName: "Brochure Cover & Text",
        variantId: "bct-1",
        variantLabel: "Cover 250gsm silk + Text 130gsm, 4pp + 16pp self-cover",
        productTypeId: 1,
      },
    ],
  },
  {
    costLine: COST_LINE_5351_INK,
    health: "red",
    productTypesAffected: [1],
    totalCheckCount: 30,
    missCount: 9,
    ourMoneyPence: 30000,
    tharsternMoneyPence: 36100,
    lastCheckedAt: isoDaysAgo(2),
    standardsRows: ["Ink coverage × sheet area, litho UV 5-colour band"],
    checkSeeds: [
      {
        dictionaryItemId: "dict-brochure-cover-text",
        dictionaryItemName: "Brochure Cover & Text",
        variantId: "bct-1",
        variantLabel: "Cover 250gsm silk + Text 130gsm, 4pp + 16pp self-cover",
        productTypeId: 1,
      },
    ],
    // 2 over, 7 under — a realistic mix rather than one uniform miss —
    // summing exactly to the aggregate Δ above (30000 − 36100 = −6100).
    missDeltasPence: [410, 150, -1230, -1100, -980, -950, -850, -800, -750],
  },
  {
    costLine: COST_LINE_5351_PRINTRUN,
    health: "green",
    productTypesAffected: [1],
    totalCheckCount: 24,
    missCount: 0,
    ourMoneyPence: 24000,
    tharsternMoneyPence: 24000,
    lastCheckedAt: isoDaysAgo(1),
    standardsRows: ["Run speed band XL UV × sheet count, litho 5-colour"],
    checkSeeds: [
      {
        dictionaryItemId: "dict-brochure-cover-text",
        dictionaryItemName: "Brochure Cover & Text",
        variantId: "bct-1",
        variantLabel: "Cover 250gsm silk + Text 130gsm, 4pp + 16pp self-cover",
        productTypeId: 1,
      },
    ],
  },
  {
    costLine: COST_LINE_801_FINISHING,
    health: "amber",
    productTypesAffected: [63],
    totalCheckCount: 22,
    missCount: 3,
    ourMoneyPence: 22000,
    tharsternMoneyPence: 23400,
    lastCheckedAt: isoDaysAgo(0, 11, 0),
    standardsRows: [
      "Guillotine setup + run, 3-side trim band",
      "Guillotine setup + run, multi-up card cutting band",
    ],
    checkSeeds: [
      {
        dictionaryItemId: "dict-business-cards",
        dictionaryItemName: "Business cards",
        variantId: "bc-1",
        variantLabel: "350gsm silk, standard",
        productTypeId: 63,
      },
    ],
  },
  {
    costLine: COST_LINE_5607_RUN,
    health: "grey",
    productTypesAffected: [44],
    totalCheckCount: 0,
    missCount: 0,
    ourMoneyPence: null,
    tharsternMoneyPence: null,
    lastCheckedAt: null,
    standardsRows: ["Hybrid press run-speed band (not yet captured)"],
    checkSeeds: [],
  },
  {
    costLine: COST_LINE_5609_RUN,
    health: "grey",
    productTypesAffected: [44],
    totalCheckCount: 0,
    missCount: 0,
    ourMoneyPence: null,
    tharsternMoneyPence: null,
    lastCheckedAt: null,
    standardsRows: ["Hybrid reel run-speed band (not yet captured)"],
    checkSeeds: [],
  },
  {
    costLine: COST_LINE_5830_PRINT,
    health: "green",
    productTypesAffected: [36, 77],
    totalCheckCount: 96,
    missCount: 2,
    ourMoneyPence: 96000,
    tharsternMoneyPence: 96340,
    lastCheckedAt: isoDaysAgo(0, 8, 30),
    standardsRows: ["Run speed band, Ricoh Pro C9500 × sheet count"],
    checkSeeds: [
      {
        dictionaryItemId: "dict-perfect-bound-book",
        dictionaryItemName: "Perfect Bound Book",
        variantId: "pbb-1",
        variantLabel: "A5 214pp, 300gsm cover matt lam, 90gsm text",
        productTypeId: 36,
      },
    ],
  },
  {
    costLine: COST_LINE_1117_PRINT,
    health: "green",
    productTypesAffected: [36],
    totalCheckCount: 64,
    missCount: 1,
    ourMoneyPence: 64000,
    tharsternMoneyPence: 64120,
    lastCheckedAt: isoDaysAgo(1),
    standardsRows: ["Run speed band, Ricoh Versafire V1 × sheet count"],
    checkSeeds: [
      {
        dictionaryItemId: "dict-perfect-bound-book",
        dictionaryItemName: "Perfect Bound Book",
        variantId: "pbb-2",
        variantLabel: "A4 128pp, 250gsm cover gloss lam, 100gsm text",
        productTypeId: 36,
      },
    ],
  },
  {
    costLine: COST_LINE_5627_PRINT,
    health: "green",
    productTypesAffected: [77],
    totalCheckCount: 30,
    missCount: 0,
    ourMoneyPence: 30000,
    tharsternMoneyPence: 30000,
    lastCheckedAt: isoDaysAgo(0, 9, 0),
    standardsRows: ["Indigo click rate × sheet count"],
    checkSeeds: [
      {
        dictionaryItemId: "dict-letterheads",
        dictionaryItemName: "Letterheads",
        variantId: "lh-2",
        variantLabel: "120gsm woven, full colour",
        productTypeId: 77,
      },
    ],
  },
  {
    costLine: COST_LINE_835_FINISHING,
    health: "green",
    productTypesAffected: [77],
    totalCheckCount: 20,
    missCount: 0,
    ourMoneyPence: 20000,
    tharsternMoneyPence: 20000,
    lastCheckedAt: isoDaysAgo(3),
    standardsRows: ["KH82 fold run-speed band × sheet count"],
    checkSeeds: [
      {
        dictionaryItemId: "dict-letterheads",
        dictionaryItemName: "Letterheads",
        variantId: "lh-1",
        variantLabel: "100gsm laid, 1-colour",
        productTypeId: 77,
      },
    ],
  },
  {
    costLine: COST_LINE_803_PRINT,
    health: "green",
    productTypesAffected: [1, 36],
    totalCheckCount: 58,
    missCount: 1,
    ourMoneyPence: 58000,
    tharsternMoneyPence: 58090,
    lastCheckedAt: isoDaysAgo(2),
    standardsRows: [
      "Run speed band L1 × sheet count, litho 5-colour",
      "Run speed band L1 × sheet count, litho 4-colour",
    ],
    checkSeeds: [
      {
        dictionaryItemId: "dict-brochure-cover-text",
        dictionaryItemName: "Brochure Cover & Text",
        variantId: "bct-1",
        variantLabel: "Cover 250gsm silk + Text 130gsm, 4pp + 16pp self-cover",
        productTypeId: 1,
      },
      {
        dictionaryItemId: "dict-perfect-bound-book",
        dictionaryItemName: "Perfect Bound Book",
        variantId: "pbb-1",
        variantLabel: "A5 214pp, 300gsm cover matt lam, 90gsm text",
        productTypeId: 36,
      },
    ],
  },
];

const costLineFixtureKey = (r: CostLineRef) =>
  `${r.costCentreId}:${r.component}`;
const costLineFixturesByKey = new Map(
  COST_LINE_FIXTURES.map((f) => [costLineFixtureKey(f.costLine), f]),
);

/** The URL-encoded `"{costCentreId}:{component}"` cost-line id — the same
 *  codec as `routing/routes.ts`'s `encodeCostLineId`/`decodeCostLineId`. */
function encodedCostLineId(r: CostLineRef): string {
  return encodeURIComponent(costLineFixtureKey(r));
}

function costLineSelf(r: CostLineRef): string {
  return `/views/live-costing/cost-lines/${encodedCostLineId(r)}`;
}

function costLineChecksUrl(r: CostLineRef): string {
  return `/views/live-costing/cost-lines/${encodedCostLineId(r)}/checks`;
}

function costCentreSetupUrl(costCentreId: number): string {
  return `/views/manufacturing/cost-centres/${costCentreId}`;
}

/** `null` for the cost-centre-less line (id `0`): there is no cost centre to
 *  navigate to, and the setup route would 404 on the sentinel. */
function costCentreUrlFor(costCentreId: number): string | null {
  return costCentreId === 0 ? null : costCentreSetupUrl(costCentreId);
}

// ── Dictionary item fixtures ─────────────────────────────────────────────

interface CellSpec {
  quantity: number;
  /** `null` on a grey cell, matching the real endpoint: the matrix reports
   *  the last check's figure, and a price with no check has none — see
   *  `docs/features/cost-engine-productionisation/08_decision_matrix_shows_checked_figures.md`. */
  costPence: number | null;
  // Excludes the cached-Tharstern-estimate states ("fresh"/"stale"/
  // "failed"/"pending"/"held_back") that widen the backend's MatrixCellState:
  // this fixture always models a checked/checking/grey cell, never a cached
  // estimate (nor a held-back one).
  state: Exclude<
    CostCheckCellState,
    "fresh" | "stale" | "failed" | "pending" | "held_back"
  >;
  costLine?: CostLineRef;
  reason?: string;
  /** Set when this exact price (item × variant × quantity) is in the
   *  rolling validation set — "pinned" (an admin chose it) or "scheduled"
   *  (the scheduler picked it to cover a gap). Omit when the price is not
   *  in the set; there is no coarser item-level flag. */
  origin?: RollingSetOrigin;
  /** When `origin` is set — defaults to a plausible recent date per origin
   *  when omitted (11 Sep for a pin, 8 Sep for a scheduled fill). */
  rollingSetAddedAt?: string;
  lastCheckedAt?: string | null;
  /** Tharstern's figure for this cell, when it differs from `costPence`.
   *  Defaults to an exact match (green) when omitted; grey cells are
   *  always `null` regardless (never checked / not priceable). */
  tharsternPence?: number;
  /** The check's echo found Tharstern used a different stock (state red). */
  substitution?: CheckSubstitution;
}

interface VariantSpec {
  variantId: string;
  displayLabel: string;
  isActive?: boolean;
  /** Set on a fixed-cost corner (spec 03 rule (d)): the same variant with
   *  these options switched on, a price of its own and its own matrix row. */
  fixedCostOptions?: FixedCostOptionChip[];
  cells: CellSpec[];
}

const PDF_PROOF_ON: FixedCostOptionChip[] = [
  { fieldKey: "Proofing", optionCode: "pdf-proofing", label: "PDF proof" },
];

interface DictionaryValidationFixture {
  dictionaryItemId: string;
  name: string;
  productTypeId: number;
  costCheckState: CostCheckState;
  mismatchingCostLine: CostLineRef | null;
  pennyExactPct: number | null;
  within5PctPct: number | null;
  lastCheckedAt: string | null;
  usedByCount: number;
  /** The item's four checks. Weekly-check membership is not one of them —
   *  it shows only in the matrix's Check now popover. */
  checklist: LiveCostingChecklistItem[];
  variants: VariantSpec[];
  lineTemplates: Record<string, CostPairLine[]>; // keyed by `${variantId}:${quantity}`
  /** The pair page's per-cost-centre "why", keyed by costCentreId —
   *  pre-rendered via `costCentreReason()` above so every line traces to
   *  exactly one `RouteReasonCode`. A press cost centre's entry carries its
   *  own `partSpecLabel` for the Alternatives expander. */
  costCentreReasons: Record<number, CostPairGroupReason>;
  pressesConsidered: PressConsidered[];
  /** "Specification as costed" — authored per (variant, quantity) pair,
   *  same keying as `lineTemplates`. Never derived from the Calculation
   *  table's quantity drivers: product type, finished size, quantity, then
   *  per-part stock/colours/pages, then finishing. */
  specTemplates: Record<string, SpecificationField[]>;
}

// Static action routes every matrix cell's Check now (and its "Also check
// weekly" box) dispatches to — the same fixed routes on every cell, matching the
// backend's own `CHECK_NOW_URL`/`ROLLING_PIN_URL` constants
// (`views_module.models.live_costing`).
const CHECK_NOW_URL = "/cost-checks/actions/check-price-now";
const ROLLING_PIN_URL = "/cost-checks/actions/pin-rolling-validation-price";

function cellsFor(spec: VariantSpec): ValidationMatrixCell[] {
  return spec.cells.map((c) => ({
    checkNowUrl: CHECK_NOW_URL,
    rollingPinUrl: ROLLING_PIN_URL,
    quantity: c.quantity,
    costPence: c.costPence,
    tharsternPence:
      c.state === "grey" ? null : (c.tharsternPence ?? c.costPence),
    state: c.state,
    costLine: c.costLine ?? null,
    reason: c.reason ?? null,
    rollingSetOrigin: c.origin ?? null,
    rollingSetAddedAt:
      c.origin == null
        ? null
        : (c.rollingSetAddedAt ??
          (c.origin === "pinned" ? isoDaysAgo(1) : isoDaysAgo(4))),
    rollingSetAddedBy: c.origin === "pinned" ? "Ben Pollard" : null,
    lastCheckedAt:
      c.lastCheckedAt !== undefined
        ? c.lastCheckedAt
        : c.state === "grey"
          ? null
          : isoDaysAgo(2),
    budgetNote: null,
    isSubstitution: c.substitution != null,
  }));
}

function countRollingSetCells(fixture: DictionaryValidationFixture): number {
  return fixture.variants
    .flatMap((v) => v.cells)
    .filter((c) => c.origin != null).length;
}

const FIXTURES: DictionaryValidationFixture[] = [
  // 1. Perfect Bound Book — PT36 — live, matches throughout.
  {
    dictionaryItemId: "dict-perfect-bound-book",
    name: "Perfect Bound Book",
    productTypeId: 36,
    costCheckState: "green",
    mismatchingCostLine: null,
    pennyExactPct: 74,
    within5PctPct: 99,
    lastCheckedAt: isoDaysAgo(0, 8, 30),
    usedByCount: 3,
    checklist: [
      {
        key: "cost_lines_matching",
        label: `${COST_LINE_803_PRINT.label}, ${COST_LINE_1117_PRINT.label} and ${COST_LINE_5830_PRINT.label} have all matched Tharstern on this item for the last 4 weeks`,
        met: true,
      },
      {
        key: "routing_validated",
        label: "Tharstern agrees with our press choice on 61 of 64 orders",
        met: true,
      },
      {
        key: "no_grey_routes",
        label:
          "Every cost line on this item's routes has been checked at least once",
        met: true,
      },
      {
        key: "no_open_red",
        label: "No cost line on this item is currently mismatching",
        met: true,
      },
    ],
    variants: [
      {
        variantId: "pbb-1",
        displayLabel: "A5 214pp, 300gsm cover matt lam, 90gsm text",
        cells: [
          {
            // Matches the "pbb-1:100" line-item breakdown's total exactly.
            quantity: 100,
            costPence: 48200,
            state: "green",
          },
          {
            // Matches the "pbb-1:250" line-item breakdown's total exactly
            // (28400 printing + 2200 paper-cover + 3800 paper-text + 5200
            // fold + 1800 guillotine + 1600 binder MR + 2800 binder run)
            // against Tharstern's 45820 (+ a 20p rounding difference on the
            // Horizon BQ470 run line, well inside the 1% Matches tolerance)
            // — the cost-pair page sums those lines, so this cell must
            // agree with that sum or the two pages show different prices
            // for the same item/variant/quantity.
            quantity: 250,
            costPence: 45800,
            tharsternPence: 45820,
            state: "green",
            origin: "scheduled",
          },
          {
            // Matches the "pbb-1:500" line-item breakdown's total exactly.
            quantity: 500,
            costPence: 89400,
            state: "green",
          },
        ],
      },
      {
        variantId: "pbb-2",
        displayLabel: "A4 128pp, 250gsm cover gloss lam, 100gsm text",
        cells: [
          {
            // Matches the "pbb-2:100" line-item breakdown's total exactly.
            quantity: 100,
            costPence: 41300,
            state: "green",
          },
          {
            // Matches the "pbb-2:250" line-item breakdown's total exactly.
            quantity: 250,
            costPence: 52700,
            state: "green",
          },
          {
            // Matches the "pbb-2:500" line-item breakdown's total exactly.
            quantity: 500,
            costPence: 74600,
            state: "green",
          },
        ],
      },
    ],
    lineTemplates: {
      "pbb-1:250": [
        {
          // MR + run split, plus the plates line under Origination —
          // coordinator review round 5.
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOMR",
          quantityDriver: "1 make-ready, litho 5-colour",
          ourTimeMinutes: 10,
          tharsternTimeMinutes: 10,
          ourValuePence: 1000,
          tharsternValuePence: 1000,
          deltaPence: 0,
          standardsRow: "Litho make-ready band, L1",
        },
        {
          costCentreId: 800,
          costCentreCode: ccCode(800),
          costHead: "origination",
          opCode: "PLATEMAKING",
          quantityDriver:
            "25 plates (cover 5-colour × 1 section + text 5-colour × 4 sections)",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 2000,
          tharsternValuePence: 2000,
          deltaPence: 0,
          standardsRow:
            "Plate cost × plate count, one plate per colour per section",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOPRINTRUN",
          quantityDriver: "2,000 sheets (SRA1)",
          ourTimeMinutes: 32,
          tharsternTimeMinutes: 32,
          ourValuePence: 25400,
          tharsternValuePence: 25400,
          deltaPence: 0,
          standardsRow: "Run speed band L1 × sheet count, litho 5-colour",
        },
        {
          // Paper per part — this pair had none until coordinator review
          // round 4.
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "250 covers, 300gsm matt lam",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 2200,
          tharsternValuePence: 2200,
          deltaPence: 0,
          standardsRow: "Stock issue, 300gsm matt lam cover × cover count",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "2,000 sheets, 90gsm text",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 3800,
          tharsternValuePence: 3800,
          deltaPence: 0,
          standardsRow: "Stock issue, 90gsm text × sheet count",
        },
        {
          costCentreId: 835,
          costCentreCode: ccCode(835),
          costHead: "finishing",
          opCode: "SECTIONFOLD",
          quantityDriver: "250 sections",
          ourTimeMinutes: 18,
          tharsternTimeMinutes: 18,
          ourValuePence: 5200,
          tharsternValuePence: 5200,
          deltaPence: 0,
          standardsRow: "KH82 fold run-speed band × section count",
        },
        {
          costCentreId: 801,
          costCentreCode: ccCode(801),
          costHead: "finishing",
          opCode: "GUILLOTINETRIM",
          quantityDriver: "250 books, 3-side trim",
          ourTimeMinutes: 9,
          tharsternTimeMinutes: 9,
          ourValuePence: 1800,
          tharsternValuePence: 1800,
          deltaPence: 0,
          standardsRow: "Guillotine setup + run, 3-side trim band",
        },
        {
          // A perfect bound book always has a perfect-binding line — the
          // Horizon BQ470 (id 5317) is the real Exeter-only binder from
          // `manufacturing-setup.ts` (processTypeId 65, which Routing
          // policy's own feasible-sites table lists as Exeter-only), so this
          // is the fixture's one genuine `site_rule` example (coordinator
          // review round 3).
          costCentreId: 5317,
          costCentreCode: ccCode(5317),
          costHead: "finishing",
          opCode: "PERFECTBINDMR",
          quantityDriver: "1 book-block set-up",
          ourTimeMinutes: 8,
          tharsternTimeMinutes: 8,
          ourValuePence: 1600,
          tharsternValuePence: 1600,
          deltaPence: 0,
          standardsRow: "Horizon BQ470 make-ready, perfect bind",
        },
        {
          costCentreId: 5317,
          costCentreCode: ccCode(5317),
          costHead: "finishing",
          opCode: "PERFECTBINDRUN",
          quantityDriver: "250 books, perfect bind",
          ourTimeMinutes: 14,
          tharsternTimeMinutes: 14.2,
          ourValuePence: 2800,
          tharsternValuePence: 2820,
          deltaPence: -20,
          standardsRow:
            "Horizon BQ470 perfect-bind run-speed band × book count",
        },
      ],
      "pbb-1:100": [
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOMR",
          quantityDriver: "1 make-ready, litho 5-colour",
          ourTimeMinutes: 10,
          tharsternTimeMinutes: 10,
          ourValuePence: 1000,
          tharsternValuePence: 1000,
          deltaPence: 0,
          standardsRow: "Litho make-ready band, L1",
        },
        {
          costCentreId: 800,
          costCentreCode: ccCode(800),
          costHead: "origination",
          opCode: "PLATEMAKING",
          quantityDriver:
            "25 plates (cover 5-colour × 1 section + text 5-colour × 4 sections)",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 2000,
          tharsternValuePence: 2000,
          deltaPence: 0,
          standardsRow:
            "Plate cost × plate count, one plate per colour per section",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOPRINTRUN",
          quantityDriver: "800 sheets (SRA1)",
          ourTimeMinutes: 8,
          tharsternTimeMinutes: 8,
          ourValuePence: 29000,
          tharsternValuePence: 29000,
          deltaPence: 0,
          standardsRow: "Run speed band L1 × sheet count, litho 5-colour",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "100 covers, 300gsm matt lam",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 900,
          tharsternValuePence: 900,
          deltaPence: 0,
          standardsRow: "Stock issue, 300gsm matt lam cover × cover count",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "800 sheets, 90gsm text",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 1400,
          tharsternValuePence: 1400,
          deltaPence: 0,
          standardsRow: "Stock issue, 90gsm text × sheet count",
        },
        {
          costCentreId: 835,
          costCentreCode: ccCode(835),
          costHead: "finishing",
          opCode: "SECTIONFOLD",
          quantityDriver: "100 sections",
          ourTimeMinutes: 8,
          tharsternTimeMinutes: 8,
          ourValuePence: 2200,
          tharsternValuePence: 2200,
          deltaPence: 0,
          standardsRow: "KH82 fold run-speed band × section count",
        },
        {
          costCentreId: 801,
          costCentreCode: ccCode(801),
          costHead: "finishing",
          opCode: "GUILLOTINETRIM",
          quantityDriver: "100 books, 3-side trim",
          ourTimeMinutes: 4,
          tharsternTimeMinutes: 4,
          ourValuePence: 800,
          tharsternValuePence: 800,
          deltaPence: 0,
          standardsRow: "Guillotine setup + run, 3-side trim band",
        },
        {
          costCentreId: 5317,
          costCentreCode: ccCode(5317),
          costHead: "finishing",
          opCode: "PERFECTBINDMR",
          quantityDriver: "1 book-block set-up",
          ourTimeMinutes: 8,
          tharsternTimeMinutes: 8,
          ourValuePence: 1600,
          tharsternValuePence: 1600,
          deltaPence: 0,
          standardsRow: "Horizon BQ470 make-ready, perfect bind",
        },
        {
          costCentreId: 5317,
          costCentreCode: ccCode(5317),
          costHead: "finishing",
          opCode: "PERFECTBINDRUN",
          quantityDriver: "100 books, perfect bind",
          ourTimeMinutes: 6,
          tharsternTimeMinutes: 6,
          ourValuePence: 9300,
          tharsternValuePence: 9300,
          deltaPence: 0,
          standardsRow:
            "Horizon BQ470 perfect-bind run-speed band × book count",
        },
      ],
      "pbb-1:500": [
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOMR",
          quantityDriver: "1 make-ready, litho 5-colour",
          ourTimeMinutes: 10,
          tharsternTimeMinutes: 10,
          ourValuePence: 1000,
          tharsternValuePence: 1000,
          deltaPence: 0,
          standardsRow: "Litho make-ready band, L1",
        },
        {
          costCentreId: 800,
          costCentreCode: ccCode(800),
          costHead: "origination",
          opCode: "PLATEMAKING",
          quantityDriver:
            "25 plates (cover 5-colour × 1 section + text 5-colour × 4 sections)",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 2000,
          tharsternValuePence: 2000,
          deltaPence: 0,
          standardsRow:
            "Plate cost × plate count, one plate per colour per section",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOPRINTRUN",
          quantityDriver: "4,000 sheets (SRA1)",
          ourTimeMinutes: 68,
          tharsternTimeMinutes: 68,
          ourValuePence: 51000,
          tharsternValuePence: 51000,
          deltaPence: 0,
          standardsRow: "Run speed band L1 × sheet count, litho 5-colour",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "500 covers, 300gsm matt lam",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 4200,
          tharsternValuePence: 4200,
          deltaPence: 0,
          standardsRow: "Stock issue, 300gsm matt lam cover × cover count",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "4,000 sheets, 90gsm text",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 7000,
          tharsternValuePence: 7000,
          deltaPence: 0,
          standardsRow: "Stock issue, 90gsm text × sheet count",
        },
        {
          costCentreId: 835,
          costCentreCode: ccCode(835),
          costHead: "finishing",
          opCode: "SECTIONFOLD",
          quantityDriver: "500 sections",
          ourTimeMinutes: 35,
          tharsternTimeMinutes: 35,
          ourValuePence: 9600,
          tharsternValuePence: 9600,
          deltaPence: 0,
          standardsRow: "KH82 fold run-speed band × section count",
        },
        {
          costCentreId: 801,
          costCentreCode: ccCode(801),
          costHead: "finishing",
          opCode: "GUILLOTINETRIM",
          quantityDriver: "500 books, 3-side trim",
          ourTimeMinutes: 17,
          tharsternTimeMinutes: 17,
          ourValuePence: 3400,
          tharsternValuePence: 3400,
          deltaPence: 0,
          standardsRow: "Guillotine setup + run, 3-side trim band",
        },
        {
          costCentreId: 5317,
          costCentreCode: ccCode(5317),
          costHead: "finishing",
          opCode: "PERFECTBINDMR",
          quantityDriver: "1 book-block set-up",
          ourTimeMinutes: 8,
          tharsternTimeMinutes: 8,
          ourValuePence: 1600,
          tharsternValuePence: 1600,
          deltaPence: 0,
          standardsRow: "Horizon BQ470 make-ready, perfect bind",
        },
        {
          costCentreId: 5317,
          costCentreCode: ccCode(5317),
          costHead: "finishing",
          opCode: "PERFECTBINDRUN",
          quantityDriver: "500 books, perfect bind",
          ourTimeMinutes: 28,
          tharsternTimeMinutes: 28,
          ourValuePence: 9600,
          tharsternValuePence: 9600,
          deltaPence: 0,
          standardsRow:
            "Horizon BQ470 perfect-bind run-speed band × book count",
        },
      ],
      "pbb-2:100": [
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOMR",
          quantityDriver: "1 make-ready, litho 5-colour",
          ourTimeMinutes: 10,
          tharsternTimeMinutes: 10,
          ourValuePence: 1000,
          tharsternValuePence: 1000,
          deltaPence: 0,
          standardsRow: "Litho make-ready band, L1",
        },
        {
          costCentreId: 800,
          costCentreCode: ccCode(800),
          costHead: "origination",
          opCode: "PLATEMAKING",
          quantityDriver:
            "25 plates (cover 5-colour × 1 section + text 5-colour × 4 sections)",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 2000,
          tharsternValuePence: 2000,
          deltaPence: 0,
          standardsRow:
            "Plate cost × plate count, one plate per colour per section",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOPRINTRUN",
          quantityDriver: "600 sheets (SRA1)",
          ourTimeMinutes: 4,
          tharsternTimeMinutes: 4,
          ourValuePence: 17000,
          tharsternValuePence: 17000,
          deltaPence: 0,
          standardsRow: "Run speed band L1 × sheet count, litho 5-colour",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "100 covers, 250gsm gloss lam",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 1300,
          tharsternValuePence: 1300,
          deltaPence: 0,
          standardsRow: "Stock issue, 250gsm gloss lam cover × cover count",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "600 sheets, 100gsm text",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 1400,
          tharsternValuePence: 1400,
          deltaPence: 0,
          standardsRow: "Stock issue, 100gsm text × sheet count",
        },
        {
          costCentreId: 835,
          costCentreCode: ccCode(835),
          costHead: "finishing",
          opCode: "SECTIONFOLD",
          quantityDriver: "100 sections",
          ourTimeMinutes: 6,
          tharsternTimeMinutes: 6,
          ourValuePence: 1900,
          tharsternValuePence: 1900,
          deltaPence: 0,
          standardsRow: "KH82 fold run-speed band × section count",
        },
        {
          costCentreId: 801,
          costCentreCode: ccCode(801),
          costHead: "finishing",
          opCode: "GUILLOTINETRIM",
          quantityDriver: "100 books, 3-side trim",
          ourTimeMinutes: 4,
          tharsternTimeMinutes: 4,
          ourValuePence: 700,
          tharsternValuePence: 700,
          deltaPence: 0,
          standardsRow: "Guillotine setup + run, 3-side trim band",
        },
        {
          costCentreId: 5317,
          costCentreCode: ccCode(5317),
          costHead: "finishing",
          opCode: "PERFECTBINDMR",
          quantityDriver: "1 book-block set-up",
          ourTimeMinutes: 8,
          tharsternTimeMinutes: 8,
          ourValuePence: 1600,
          tharsternValuePence: 1600,
          deltaPence: 0,
          standardsRow: "Horizon BQ470 make-ready, perfect bind",
        },
        {
          costCentreId: 5317,
          costCentreCode: ccCode(5317),
          costHead: "finishing",
          opCode: "PERFECTBINDRUN",
          quantityDriver: "100 books, perfect bind",
          ourTimeMinutes: 6,
          tharsternTimeMinutes: 6,
          ourValuePence: 14400,
          tharsternValuePence: 14400,
          deltaPence: 0,
          standardsRow:
            "Horizon BQ470 perfect-bind run-speed band × book count",
        },
      ],
      "pbb-2:250": [
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOMR",
          quantityDriver: "1 make-ready, litho 5-colour",
          ourTimeMinutes: 10,
          tharsternTimeMinutes: 10,
          ourValuePence: 1000,
          tharsternValuePence: 1000,
          deltaPence: 0,
          standardsRow: "Litho make-ready band, L1",
        },
        {
          costCentreId: 800,
          costCentreCode: ccCode(800),
          costHead: "origination",
          opCode: "PLATEMAKING",
          quantityDriver:
            "25 plates (cover 5-colour × 1 section + text 5-colour × 4 sections)",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 2000,
          tharsternValuePence: 2000,
          deltaPence: 0,
          standardsRow:
            "Plate cost × plate count, one plate per colour per section",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOPRINTRUN",
          quantityDriver: "1,500 sheets (SRA1)",
          ourTimeMinutes: 22,
          tharsternTimeMinutes: 22,
          ourValuePence: 27000,
          tharsternValuePence: 27000,
          deltaPence: 0,
          standardsRow: "Run speed band L1 × sheet count, litho 5-colour",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "250 covers, 250gsm gloss lam",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 2600,
          tharsternValuePence: 2600,
          deltaPence: 0,
          standardsRow: "Stock issue, 250gsm gloss lam cover × cover count",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "1,500 sheets, 100gsm text",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 3300,
          tharsternValuePence: 3300,
          deltaPence: 0,
          standardsRow: "Stock issue, 100gsm text × sheet count",
        },
        {
          costCentreId: 835,
          costCentreCode: ccCode(835),
          costHead: "finishing",
          opCode: "SECTIONFOLD",
          quantityDriver: "250 sections",
          ourTimeMinutes: 14,
          tharsternTimeMinutes: 14,
          ourValuePence: 4200,
          tharsternValuePence: 4200,
          deltaPence: 0,
          standardsRow: "KH82 fold run-speed band × section count",
        },
        {
          costCentreId: 801,
          costCentreCode: ccCode(801),
          costHead: "finishing",
          opCode: "GUILLOTINETRIM",
          quantityDriver: "250 books, 3-side trim",
          ourTimeMinutes: 9,
          tharsternTimeMinutes: 9,
          ourValuePence: 1500,
          tharsternValuePence: 1500,
          deltaPence: 0,
          standardsRow: "Guillotine setup + run, 3-side trim band",
        },
        {
          costCentreId: 5317,
          costCentreCode: ccCode(5317),
          costHead: "finishing",
          opCode: "PERFECTBINDMR",
          quantityDriver: "1 book-block set-up",
          ourTimeMinutes: 8,
          tharsternTimeMinutes: 8,
          ourValuePence: 1600,
          tharsternValuePence: 1600,
          deltaPence: 0,
          standardsRow: "Horizon BQ470 make-ready, perfect bind",
        },
        {
          costCentreId: 5317,
          costCentreCode: ccCode(5317),
          costHead: "finishing",
          opCode: "PERFECTBINDRUN",
          quantityDriver: "250 books, perfect bind",
          ourTimeMinutes: 14,
          tharsternTimeMinutes: 14,
          ourValuePence: 9500,
          tharsternValuePence: 9500,
          deltaPence: 0,
          standardsRow:
            "Horizon BQ470 perfect-bind run-speed band × book count",
        },
      ],
      "pbb-2:500": [
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOMR",
          quantityDriver: "1 make-ready, litho 5-colour",
          ourTimeMinutes: 10,
          tharsternTimeMinutes: 10,
          ourValuePence: 1000,
          tharsternValuePence: 1000,
          deltaPence: 0,
          standardsRow: "Litho make-ready band, L1",
        },
        {
          costCentreId: 800,
          costCentreCode: ccCode(800),
          costHead: "origination",
          opCode: "PLATEMAKING",
          quantityDriver:
            "25 plates (cover 5-colour × 1 section + text 5-colour × 4 sections)",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 2000,
          tharsternValuePence: 2000,
          deltaPence: 0,
          standardsRow:
            "Plate cost × plate count, one plate per colour per section",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOPRINTRUN",
          quantityDriver: "3,000 sheets (SRA1)",
          ourTimeMinutes: 48,
          tharsternTimeMinutes: 48,
          ourValuePence: 41000,
          tharsternValuePence: 41000,
          deltaPence: 0,
          standardsRow: "Run speed band L1 × sheet count, litho 5-colour",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "500 covers, 250gsm gloss lam",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 4900,
          tharsternValuePence: 4900,
          deltaPence: 0,
          standardsRow: "Stock issue, 250gsm gloss lam cover × cover count",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "3,000 sheets, 100gsm text",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 6200,
          tharsternValuePence: 6200,
          deltaPence: 0,
          standardsRow: "Stock issue, 100gsm text × sheet count",
        },
        {
          costCentreId: 835,
          costCentreCode: ccCode(835),
          costHead: "finishing",
          opCode: "SECTIONFOLD",
          quantityDriver: "500 sections",
          ourTimeMinutes: 27,
          tharsternTimeMinutes: 27,
          ourValuePence: 7800,
          tharsternValuePence: 7800,
          deltaPence: 0,
          standardsRow: "KH82 fold run-speed band × section count",
        },
        {
          costCentreId: 801,
          costCentreCode: ccCode(801),
          costHead: "finishing",
          opCode: "GUILLOTINETRIM",
          quantityDriver: "500 books, 3-side trim",
          ourTimeMinutes: 17,
          tharsternTimeMinutes: 17,
          ourValuePence: 2800,
          tharsternValuePence: 2800,
          deltaPence: 0,
          standardsRow: "Guillotine setup + run, 3-side trim band",
        },
        {
          costCentreId: 5317,
          costCentreCode: ccCode(5317),
          costHead: "finishing",
          opCode: "PERFECTBINDMR",
          quantityDriver: "1 book-block set-up",
          ourTimeMinutes: 8,
          tharsternTimeMinutes: 8,
          ourValuePence: 1600,
          tharsternValuePence: 1600,
          deltaPence: 0,
          standardsRow: "Horizon BQ470 make-ready, perfect bind",
        },
        {
          costCentreId: 5317,
          costCentreCode: ccCode(5317),
          costHead: "finishing",
          opCode: "PERFECTBINDRUN",
          quantityDriver: "500 books, perfect bind",
          ourTimeMinutes: 28,
          tharsternTimeMinutes: 28,
          ourValuePence: 7300,
          tharsternValuePence: 7300,
          deltaPence: 0,
          standardsRow:
            "Horizon BQ470 perfect-bind run-speed band × book count",
        },
      ],
    },
    costCentreReasons: {
      803: costCentreReason(
        "cheapest_suitable",
        { suitableCount: 2 },
        "Text · 90 gsm",
      ),
      // Three finishing cost centres under one head, each with its own why —
      // never a single shared "finishing" reason.
      835: costCentreReason("step_map_default"),
      801: costCentreReason("only_suitable", { kind: "cost_centre" }),
      // The fixture's one genuine `site_rule` reason (coordinator review
      // round 3) — the binder is Exeter-only per Routing policy's own
      // feasible-sites table, not a cost/step-map choice.
      5317: costCentreReason(
        "site_rule",
        { site: "Exeter" },
        "Book block · 214pp perfect bind",
      ),
      // Plate-making follows the chosen press's own site — only one
      // suitable plate room on that site.
      800: costCentreReason("only_suitable", { kind: "cost_centre" }),
    },
    pressesConsidered: [
      {
        ...pressRef(803),
        site: "Exeter",
        outcome: "chosen",
        reason:
          "cheapest of 2 suitable presses — also keeps printing on the same site as this job's Exeter-only perfect binder",
        wholeRouteCostPence: 28400,
      },
      {
        ...pressRef(5830),
        site: "Buckingham",
        outcome: "suitable_not_chosen",
        reason:
          "suitable but not cheapest — Buckingham adds carriage to Exeter finishing",
        wholeRouteCostPence: 30100,
      },
    ],
    specTemplates: {
      "pbb-1:250": [
        {
          label: "Product type",
          weSubmitted: "Perfect Bound Book (PT36)",
          tharsternCosted: "Perfect Bound Book (PT36)",
          differs: false,
        },
        {
          label: "Finished size",
          weSubmitted: "A5",
          tharsternCosted: "A5",
          differs: false,
        },
        {
          label: "Quantity",
          weSubmitted: "250",
          tharsternCosted: "250",
          differs: false,
        },
        {
          label: "Cover stock",
          weSubmitted: "300 gsm, matt laminated",
          tharsternCosted: "300 gsm, matt laminated",
          differs: false,
        },
        {
          label: "Cover colours",
          weSubmitted: "4/0",
          tharsternCosted: "4/0",
          differs: false,
        },
        {
          label: "Text stock",
          weSubmitted: "90 gsm",
          tharsternCosted: "90 gsm",
          differs: false,
        },
        {
          label: "Text colours",
          weSubmitted: "4/4",
          tharsternCosted: "4/4",
          differs: false,
        },
        {
          label: "Text pages",
          weSubmitted: "214pp",
          tharsternCosted: "214pp",
          differs: false,
        },
        {
          label: "Finishing",
          weSubmitted: "Fold, guillotine trim, perfect bind",
          tharsternCosted: "Fold, guillotine trim, perfect bind",
          differs: false,
        },
      ],
      "pbb-1:100": [
        {
          label: "Product type",
          weSubmitted: "Perfect Bound Book (PT36)",
          tharsternCosted: "Perfect Bound Book (PT36)",
          differs: false,
        },
        {
          label: "Finished size",
          weSubmitted: "A5",
          tharsternCosted: "A5",
          differs: false,
        },
        {
          label: "Quantity",
          weSubmitted: "100",
          tharsternCosted: "100",
          differs: false,
        },
        {
          label: "Cover stock",
          weSubmitted: "300 gsm, matt laminated",
          tharsternCosted: "300 gsm, matt laminated",
          differs: false,
        },
        {
          label: "Cover colours",
          weSubmitted: "4/0",
          tharsternCosted: "4/0",
          differs: false,
        },
        {
          label: "Text stock",
          weSubmitted: "90 gsm",
          tharsternCosted: "90 gsm",
          differs: false,
        },
        {
          label: "Text colours",
          weSubmitted: "4/4",
          tharsternCosted: "4/4",
          differs: false,
        },
        {
          label: "Text pages",
          weSubmitted: "214pp",
          tharsternCosted: "214pp",
          differs: false,
        },
        {
          label: "Finishing",
          weSubmitted: "Fold, guillotine trim, perfect bind",
          tharsternCosted: "Fold, guillotine trim, perfect bind",
          differs: false,
        },
      ],
      "pbb-1:500": [
        {
          label: "Product type",
          weSubmitted: "Perfect Bound Book (PT36)",
          tharsternCosted: "Perfect Bound Book (PT36)",
          differs: false,
        },
        {
          label: "Finished size",
          weSubmitted: "A5",
          tharsternCosted: "A5",
          differs: false,
        },
        {
          label: "Quantity",
          weSubmitted: "500",
          tharsternCosted: "500",
          differs: false,
        },
        {
          label: "Cover stock",
          weSubmitted: "300 gsm, matt laminated",
          tharsternCosted: "300 gsm, matt laminated",
          differs: false,
        },
        {
          label: "Cover colours",
          weSubmitted: "4/0",
          tharsternCosted: "4/0",
          differs: false,
        },
        {
          label: "Text stock",
          weSubmitted: "90 gsm",
          tharsternCosted: "90 gsm",
          differs: false,
        },
        {
          label: "Text colours",
          weSubmitted: "4/4",
          tharsternCosted: "4/4",
          differs: false,
        },
        {
          label: "Text pages",
          weSubmitted: "214pp",
          tharsternCosted: "214pp",
          differs: false,
        },
        {
          label: "Finishing",
          weSubmitted: "Fold, guillotine trim, perfect bind",
          tharsternCosted: "Fold, guillotine trim, perfect bind",
          differs: false,
        },
      ],
      "pbb-2:100": [
        {
          label: "Product type",
          weSubmitted: "Perfect Bound Book (PT36)",
          tharsternCosted: "Perfect Bound Book (PT36)",
          differs: false,
        },
        {
          label: "Finished size",
          weSubmitted: "A4",
          tharsternCosted: "A4",
          differs: false,
        },
        {
          label: "Quantity",
          weSubmitted: "100",
          tharsternCosted: "100",
          differs: false,
        },
        {
          label: "Cover stock",
          weSubmitted: "250 gsm, gloss laminated",
          tharsternCosted: "250 gsm, gloss laminated",
          differs: false,
        },
        {
          label: "Cover colours",
          weSubmitted: "4/0",
          tharsternCosted: "4/0",
          differs: false,
        },
        {
          label: "Text stock",
          weSubmitted: "100 gsm",
          tharsternCosted: "100 gsm",
          differs: false,
        },
        {
          label: "Text colours",
          weSubmitted: "4/4",
          tharsternCosted: "4/4",
          differs: false,
        },
        {
          label: "Text pages",
          weSubmitted: "128pp",
          tharsternCosted: "128pp",
          differs: false,
        },
        {
          label: "Finishing",
          weSubmitted: "Fold, guillotine trim, perfect bind",
          tharsternCosted: "Fold, guillotine trim, perfect bind",
          differs: false,
        },
      ],
      "pbb-2:250": [
        {
          label: "Product type",
          weSubmitted: "Perfect Bound Book (PT36)",
          tharsternCosted: "Perfect Bound Book (PT36)",
          differs: false,
        },
        {
          label: "Finished size",
          weSubmitted: "A4",
          tharsternCosted: "A4",
          differs: false,
        },
        {
          label: "Quantity",
          weSubmitted: "250",
          tharsternCosted: "250",
          differs: false,
        },
        {
          label: "Cover stock",
          weSubmitted: "250 gsm, gloss laminated",
          tharsternCosted: "250 gsm, gloss laminated",
          differs: false,
        },
        {
          label: "Cover colours",
          weSubmitted: "4/0",
          tharsternCosted: "4/0",
          differs: false,
        },
        {
          label: "Text stock",
          weSubmitted: "100 gsm",
          tharsternCosted: "100 gsm",
          differs: false,
        },
        {
          label: "Text colours",
          weSubmitted: "4/4",
          tharsternCosted: "4/4",
          differs: false,
        },
        {
          label: "Text pages",
          weSubmitted: "128pp",
          tharsternCosted: "128pp",
          differs: false,
        },
        {
          label: "Finishing",
          weSubmitted: "Fold, guillotine trim, perfect bind",
          tharsternCosted: "Fold, guillotine trim, perfect bind",
          differs: false,
        },
      ],
      "pbb-2:500": [
        {
          label: "Product type",
          weSubmitted: "Perfect Bound Book (PT36)",
          tharsternCosted: "Perfect Bound Book (PT36)",
          differs: false,
        },
        {
          label: "Finished size",
          weSubmitted: "A4",
          tharsternCosted: "A4",
          differs: false,
        },
        {
          label: "Quantity",
          weSubmitted: "500",
          tharsternCosted: "500",
          differs: false,
        },
        {
          label: "Cover stock",
          weSubmitted: "250 gsm, gloss laminated",
          tharsternCosted: "250 gsm, gloss laminated",
          differs: false,
        },
        {
          label: "Cover colours",
          weSubmitted: "4/0",
          tharsternCosted: "4/0",
          differs: false,
        },
        {
          label: "Text stock",
          weSubmitted: "100 gsm",
          tharsternCosted: "100 gsm",
          differs: false,
        },
        {
          label: "Text colours",
          weSubmitted: "4/4",
          tharsternCosted: "4/4",
          differs: false,
        },
        {
          label: "Text pages",
          weSubmitted: "128pp",
          tharsternCosted: "128pp",
          differs: false,
        },
        {
          label: "Finishing",
          weSubmitted: "Fold, guillotine trim, perfect bind",
          tharsternCosted: "Fold, guillotine trim, perfect bind",
          differs: false,
        },
      ],
    },
  },

  // 2. Brochure Cover & Text — PT1 — mismatching cost line 5351 · Ink.
  {
    dictionaryItemId: "dict-brochure-cover-text",
    name: "Brochure Cover & Text",
    productTypeId: 1,
    costCheckState: "red",
    mismatchingCostLine: COST_LINE_5351_INK,
    pennyExactPct: 41,
    within5PctPct: 92,
    lastCheckedAt: isoDaysAgo(2),
    usedByCount: 5,
    checklist: [
      {
        key: "cost_lines_matching",
        label: `${COST_LINE_5351_INK.label} has mismatched Tharstern in the last 4 weeks (30 checks, 9 differ)`,
        met: false,
      },
      {
        key: "routing_validated",
        label: "Tharstern agrees with our press choice on 26 of 28 orders",
        met: true,
      },
      {
        key: "no_grey_routes",
        label:
          "Every cost line on this item's routes has been checked at least once",
        met: true,
      },
      {
        // The link-out to the by-cost-line view lives on the page
        // (LiveCostingPanel), keyed off `no_open_red`+`!met` — mock data
        // carries only the semantic fact, never a raw route string (same
        // rule the routing-policy link follows: pages build links from the
        // static route manifest, never from a URL stored in a fixture).
        key: "no_open_red",
        label: `${COST_LINE_5351_INK.label} is currently mismatching`,
        met: false,
      },
    ],
    variants: [
      {
        variantId: "bct-1",
        displayLabel: "Cover 250gsm silk + Text 130gsm, 4pp + 16pp self-cover",
        cells: [
          {
            // Same -6.1% ratio at every quantity — small on any one line,
            // but systematic across the whole item, which is what makes
            // this cost line a mismatch rather than an amber one-off.
            quantity: 250,
            costPence: 38900,
            tharsternPence: 41429,
            state: "red",
            costLine: COST_LINE_5351_INK,
            reason:
              "Ink-weight line diverges from Tharstern's workings — systematic, not a one-off",
            origin: "pinned",
          },
          {
            // 61200/65178 (the ink-weight Δ alone) plus a paper line per
            // part and the saddle-stitch line this pair was missing —
            // 14100 on both sides, exact match, so the −3978 Δ still comes
            // entirely from the ink line (coordinator review round 4).
            quantity: 500,
            costPence: 75300,
            tharsternPence: 79278,
            state: "red",
            costLine: COST_LINE_5351_INK,
            reason:
              "Ink-weight line diverges from Tharstern's workings — systematic, not a one-off",
            origin: "pinned",
          },
          {
            quantity: 1000,
            costPence: 98700,
            tharsternPence: 105116,
            state: "red",
            costLine: COST_LINE_5351_INK,
            reason:
              "Ink-weight line diverges from Tharstern's workings — systematic, not a one-off",
          },
        ],
      },
      {
        variantId: "bct-2",
        displayLabel: "Cover 300gsm uncoated + Text 150gsm, 4pp + 8pp",
        cells: [
          { quantity: 250, costPence: 34500, state: "green" },
          { quantity: 500, costPence: 55800, state: "green" },
          { quantity: 1000, costPence: 89100, state: "green" },
        ],
      },
    ],
    lineTemplates: {
      "bct-1:500": [
        {
          costCentreId: 5351,
          costCentreCode: ccCode(5351),
          costHead: "other_material",
          opCode: "LITHOUVPROCESSBLACK",
          quantityDriver: "0.2707 kg process black ink",
          // The book-block ink-weight issue (under investigation) —
          // Tharstern's own workings read a fractionally heavier coverage
          // for the same job, which is the root of this cost line's
          // systematic mismatch.
          tharsternQuantityDriver: "0.2712 kg process black ink",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          // A near-identical ink weight (0.2707kg vs 0.2712kg) but a much
          // larger cost gap — Tharstern's workings apply a different rate
          // to that fractional excess (the ink-weight issue is a rate
          // lookup defect, not a linear function of the weight difference).
          // Sized so this line plus the two exact-match printing lines
          // below sum to the matrix cell's 61200/65178 (-6.1%) — the same
          // total the cost-pair page's summary strip shows.
          ourValuePence: 30700,
          tharsternValuePence: 34678,
          deltaPence: -3978,
          standardsRow:
            "Ink coverage × sheet area, litho UV 5-colour band (book-block ink-weight difference under investigation)",
        },
        {
          // MR + run split, plus the plates line under Origination — this
          // litho press had one bundled "printing" line until coordinator
          // review round 5.
          costCentreId: 5351,
          costCentreCode: ccCode(5351),
          costHead: "printing",
          opCode: "LITHOUVMR",
          quantityDriver: "1 make-ready, litho UV 5-colour",
          ourTimeMinutes: 5,
          tharsternTimeMinutes: 5,
          ourValuePence: 500,
          tharsternValuePence: 500,
          deltaPence: 0,
          standardsRow: "Litho UV make-ready band, XL75",
        },
        {
          costCentreId: 800,
          costCentreCode: ccCode(800),
          costHead: "origination",
          opCode: "PLATEMAKING",
          quantityDriver: "4 plates (cover 4/0, 1 section)",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 400,
          tharsternValuePence: 400,
          deltaPence: 0,
          standardsRow:
            "Plate cost × plate count, one plate per colour per section",
        },
        {
          costCentreId: 5351,
          costCentreCode: ccCode(5351),
          costHead: "printing",
          opCode: "LITHOUVPRINTRUN",
          quantityDriver: "1,040 sheets (SRA1)",
          ourTimeMinutes: 19,
          tharsternTimeMinutes: 19,
          ourValuePence: 20700,
          tharsternValuePence: 20700,
          deltaPence: 0,
          standardsRow: "Run speed band XL UV × sheet count, litho 5-colour",
        },
        {
          // Was mistakenly keyed to 803 (a litho press already used for the
          // cover in fixture 1) — the text press per `pressesConsidered`
          // below, and per the drilldown line's own worked example, is 5830
          // (Ricoh Pro C9500, a digital press). A charged line on a press
          // `pressesConsidered` doesn't list as chosen would be a
          // self-contradicting Calculation table.
          costCentreId: 5830,
          costCentreCode: ccCode(5830),
          costHead: "printing",
          opCode: "DIGITALMR",
          quantityDriver: "1 make-ready, digital",
          ourTimeMinutes: 3,
          tharsternTimeMinutes: 3,
          ourValuePence: 250,
          tharsternValuePence: 250,
          deltaPence: 0,
          standardsRow: "Digital make-ready band, Ricoh Pro C9500",
        },
        {
          costCentreId: 5830,
          costCentreCode: ccCode(5830),
          costHead: "printing",
          opCode: "DIGITALPRINTRUN",
          quantityDriver: "500 sheets (SRA1)",
          ourTimeMinutes: 8,
          tharsternTimeMinutes: 8,
          ourValuePence: 8650,
          tharsternValuePence: 8650,
          deltaPence: 0,
          standardsRow: "Run speed band, Ricoh Pro C9500 × sheet count",
        },
        // Paper per part, and the saddle-stitch finishing line the spec
        // calls for — this pair had neither (coordinator review round 4).
        // Billed through the same press cost centre that already runs each
        // part, matching the ink line's own convention above.
        {
          costCentreId: 5351,
          costCentreCode: ccCode(5351),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "1,040 sheets, 250gsm silk board",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 4200,
          tharsternValuePence: 4200,
          deltaPence: 0,
          standardsRow: "Stock issue, 250gsm silk board × sheet count",
        },
        {
          costCentreId: 5830,
          costCentreCode: ccCode(5830),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "500 sheets, 130gsm silk text",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 6800,
          tharsternValuePence: 6800,
          deltaPence: 0,
          standardsRow: "Stock issue, 130gsm silk text × sheet count",
        },
        {
          costCentreId: 830,
          costCentreCode: ccCode(830),
          costHead: "finishing",
          opCode: "SADDLESTITCH",
          quantityDriver: "500 books, 2-stitch saddle",
          ourTimeMinutes: 7,
          tharsternTimeMinutes: 7,
          ourValuePence: 3100,
          tharsternValuePence: 3100,
          deltaPence: 0,
          standardsRow: "Saddle stitcher run-speed band × book count",
        },
      ],
    },
    costCentreReasons: {
      // The cover and text presses are two different cost centres under
      // one "printing" head, each with its own why — never a single shared
      // "printing" reason (the same distinction the finishing example
      // makes: a binder and a guillotine, or here, two different presses).
      5351: costCentreReason(
        "cheapest_suitable",
        { suitableCount: 2 },
        "Cover · 250 gsm silk · 4/0",
      ),
      5830: costCentreReason(
        "cheapest_suitable",
        { suitableCount: 1 },
        "Text · 130 gsm silk · 4/0",
      ),
      // Stitching is feasible at both sites (Routing policy's own
      // feasible-sites table), so this is the step map's default choice,
      // not a site rule (same reasoning as fixture 1's guillotine).
      830: costCentreReason("step_map_default"),
      // Plate-making follows the cover press's own site — only one
      // suitable plate room on that site.
      800: costCentreReason("only_suitable", { kind: "cost_centre" }),
    },
    pressesConsidered: [
      {
        ...pressRef(5351),
        site: "Exeter",
        outcome: "chosen",
        reason: "cheapest suitable press for the cover",
        wholeRouteCostPence: 21600,
      },
      {
        ...pressRef(5830),
        site: "Buckingham",
        outcome: "chosen",
        reason: "cheapest suitable press for the text",
        wholeRouteCostPence: 21200,
      },
      {
        ...pressRef(803),
        site: "Exeter",
        outcome: "suitable_not_chosen",
        reason: "suitable for the cover but not the cheapest",
        wholeRouteCostPence: 23800,
      },
    ],
    specTemplates: {
      "bct-1:500": [
        {
          label: "Product type",
          weSubmitted: "Brochure Cover & Text (PT1)",
          tharsternCosted: "Brochure Cover & Text (PT1)",
          differs: false,
        },
        {
          label: "Finished size",
          weSubmitted: "A4",
          tharsternCosted: "A4",
          differs: false,
        },
        {
          label: "Quantity",
          weSubmitted: "500",
          tharsternCosted: "500",
          differs: false,
        },
        {
          label: "Cover stock",
          weSubmitted: "250 gsm silk",
          tharsternCosted: "250 gsm silk",
          differs: false,
        },
        {
          label: "Cover colours",
          weSubmitted: "4/4",
          tharsternCosted: "4/4",
          differs: false,
        },
        {
          label: "Cover pages",
          weSubmitted: "4pp",
          tharsternCosted: "4pp",
          differs: false,
        },
        {
          label: "Text stock",
          weSubmitted: "130 gsm silk",
          tharsternCosted: "130 gsm silk",
          differs: false,
        },
        {
          label: "Text colours",
          weSubmitted: "4/4",
          tharsternCosted: "4/4",
          differs: false,
        },
        {
          label: "Text pages",
          weSubmitted: "16pp self-cover",
          tharsternCosted: "16pp self-cover",
          differs: false,
        },
        {
          label: "Finishing",
          weSubmitted: "Saddle-stitch",
          tharsternCosted: "Saddle-stitch",
          differs: false,
        },
      ],
    },
  },

  // 3. Business cards — PT63 — small difference on 801 finishing run; bc-2
  // mismatches on paper because Tharstern substitutes its stock.
  {
    dictionaryItemId: "dict-business-cards",
    name: "Business cards",
    productTypeId: 63,
    costCheckState: "red",
    mismatchingCostLine: COST_LINE_PAPER,
    pennyExactPct: 68,
    within5PctPct: 99,
    lastCheckedAt: isoDaysAgo(0, 11, 0),
    usedByCount: 8,
    checklist: [
      {
        key: "cost_lines_matching",
        label: `${COST_LINE_801_FINISHING.label} has a small, non-systematic difference from Tharstern — doesn't block live costing`,
        met: true,
      },
      {
        key: "routing_validated",
        label: "Tharstern agrees with our press choice on 21 of 22 orders",
        met: true,
      },
      {
        key: "no_grey_routes",
        label:
          "Every cost line on this item's routes has been checked at least once",
        met: true,
      },
      {
        key: "no_open_red",
        label: `${COST_LINE_PAPER.label} is mismatching on 400gsm uncoated, soft-touch lam — Tharstern substitutes the stock`,
        met: false,
      },
    ],
    variants: [
      {
        variantId: "bc-1",
        displayLabel: "350gsm silk, standard",
        cells: [
          {
            quantity: 250,
            costPence: 3200,
            tharsternPence: 3328,
            state: "amber",
            costLine: COST_LINE_801_FINISHING,
            reason:
              "Guillotine run time ~4% under standards — small, non-systematic",
          },
          {
            // Matches the "bc-1:500" line-item breakdown's total exactly
            // (1100 guillotine + 2600 litho run + 1300 paper ours; 1160 +
            // 2600 + 1300 Tharstern — paper added in coordinator review
            // round 4) — the cost-pair page sums those lines, so this cell
            // must agree with that sum.
            quantity: 500,
            costPence: 5000,
            tharsternPence: 5060,
            state: "amber",
            costLine: COST_LINE_801_FINISHING,
            reason:
              "Guillotine run time ~4% under standards — small, non-systematic",
          },
          {
            // Matches the "bc-1:1000" line-item breakdown's total exactly
            // (4600 printing + 2400 paper + 900 guillotine, exact match).
            quantity: 1000,
            costPence: 7900,
            state: "green",
          },
        ],
      },
      {
        variantId: "bc-2",
        displayLabel: "400gsm uncoated, soft-touch lam",
        // Tharstern's echo substitutes the stock on every quantity, so every
        // check is red on paper — the same substitution the Mappings tab
        // shows on this option's Tharstern leaf.
        cells: [
          {
            // Matches the "bc-2:250" line-item breakdown's totals exactly
            // (1300 printing + 700 paper + 1200 lamination + 900 guillotine
            // ours; paper 790 on Tharstern's substituted stock).
            quantity: 250,
            costPence: 4100,
            tharsternPence: 4190,
            state: "red",
            costLine: COST_LINE_PAPER,
            substitution: BC_2_SUBSTITUTION,
            origin: "pinned",
          },
          {
            // (2600 printing + 1500 paper + 1400 lamination + 700
            // guillotine ours; paper 1690 on Tharstern's stock.)
            quantity: 500,
            costPence: 6200,
            tharsternPence: 6390,
            state: "red",
            costLine: COST_LINE_PAPER,
            substitution: BC_2_SUBSTITUTION,
          },
          {
            // (4600 printing + 2400 paper + 1800 lamination + 1000
            // guillotine ours; paper 2700 on Tharstern's stock.)
            quantity: 1000,
            costPence: 9800,
            tharsternPence: 10100,
            state: "red",
            costLine: COST_LINE_PAPER,
            substitution: BC_2_SUBSTITUTION,
          },
        ],
      },
    ],
    lineTemplates: {
      "bc-1:250": [
        {
          // MR + run split, plus the plates line under Origination — this
          // press had one bundled "printing" line until coordinator review
          // round 5.
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOMR",
          quantityDriver: "1 make-ready, litho 4-colour",
          ourTimeMinutes: 3,
          tharsternTimeMinutes: 3,
          ourValuePence: 400,
          tharsternValuePence: 400,
          deltaPence: 0,
          standardsRow: "Litho make-ready band, L1",
        },
        {
          costCentreId: 800,
          costCentreCode: ccCode(800),
          costHead: "origination",
          opCode: "PLATEMAKING",
          quantityDriver: "8 plates (4/4, 1 section)",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 800,
          tharsternValuePence: 800,
          deltaPence: 0,
          standardsRow:
            "Plate cost × plate count, one plate per colour per section",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOPRINTRUN",
          quantityDriver: "18 sheets (SRA3, 14-up)",
          ourTimeMinutes: 1,
          tharsternTimeMinutes: 1,
          ourValuePence: 100,
          tharsternValuePence: 100,
          deltaPence: 0,
          standardsRow: "Run speed band L1 × sheet count, litho 4-colour",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "18 sheets, 350gsm silk board",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 600,
          tharsternValuePence: 600,
          deltaPence: 0,
          standardsRow: "Stock issue, 350gsm silk board × sheet count",
        },
        {
          costCentreId: 801,
          costCentreCode: ccCode(801),
          costHead: "finishing",
          opCode: "GUILLOTINETRIM",
          quantityDriver: "4 cutting stacks, 250 cards",
          ourTimeMinutes: 3,
          tharsternTimeMinutes: 3.1,
          ourValuePence: 1300,
          tharsternValuePence: 1428,
          deltaPence: -128,
          standardsRow: "Guillotine setup + run, multi-up card cutting band",
        },
      ],
      "bc-1:500": [
        {
          costCentreId: 801,
          costCentreCode: ccCode(801),
          costHead: "finishing",
          opCode: "GUILLOTINETRIM",
          quantityDriver: "4 cutting stacks, 500 cards",
          ourTimeMinutes: 6,
          tharsternTimeMinutes: 6.3,
          ourValuePence: 1100,
          tharsternValuePence: 1160,
          deltaPence: -60,
          standardsRow: "Guillotine setup + run, multi-up card cutting band",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOMR",
          quantityDriver: "1 make-ready, litho 4-colour",
          ourTimeMinutes: 3,
          tharsternTimeMinutes: 3,
          ourValuePence: 400,
          tharsternValuePence: 400,
          deltaPence: 0,
          standardsRow: "Litho make-ready band, L1",
        },
        {
          costCentreId: 800,
          costCentreCode: ccCode(800),
          costHead: "origination",
          opCode: "PLATEMAKING",
          quantityDriver: "8 plates (4/4, 1 section)",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 800,
          tharsternValuePence: 800,
          deltaPence: 0,
          standardsRow:
            "Plate cost × plate count, one plate per colour per section",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOPRINTRUN",
          quantityDriver: "36 sheets (SRA3, 14-up)",
          ourTimeMinutes: 3,
          tharsternTimeMinutes: 3,
          ourValuePence: 1400,
          tharsternValuePence: 1400,
          deltaPence: 0,
          standardsRow: "Run speed band L1 × sheet count, litho 4-colour",
        },
        {
          // Paper line — this pair had none until coordinator review round 4.
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "36 sheets, 350gsm silk board",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 1300,
          tharsternValuePence: 1300,
          deltaPence: 0,
          standardsRow: "Stock issue, 350gsm silk board × sheet count",
        },
      ],
      "bc-1:1000": [
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOMR",
          quantityDriver: "1 make-ready, litho 4-colour",
          ourTimeMinutes: 3,
          tharsternTimeMinutes: 3,
          ourValuePence: 400,
          tharsternValuePence: 400,
          deltaPence: 0,
          standardsRow: "Litho make-ready band, L1",
        },
        {
          costCentreId: 800,
          costCentreCode: ccCode(800),
          costHead: "origination",
          opCode: "PLATEMAKING",
          quantityDriver: "8 plates (4/4, 1 section)",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 800,
          tharsternValuePence: 800,
          deltaPence: 0,
          standardsRow:
            "Plate cost × plate count, one plate per colour per section",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOPRINTRUN",
          quantityDriver: "72 sheets (SRA3, 14-up)",
          ourTimeMinutes: 9,
          tharsternTimeMinutes: 9,
          ourValuePence: 3400,
          tharsternValuePence: 3400,
          deltaPence: 0,
          standardsRow: "Run speed band L1 × sheet count, litho 4-colour",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "72 sheets, 350gsm silk board",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 2400,
          tharsternValuePence: 2400,
          deltaPence: 0,
          standardsRow: "Stock issue, 350gsm silk board × sheet count",
        },
        {
          costCentreId: 801,
          costCentreCode: ccCode(801),
          costHead: "finishing",
          opCode: "GUILLOTINETRIM",
          quantityDriver: "8 cutting stacks, 1000 cards",
          ourTimeMinutes: 10,
          tharsternTimeMinutes: 10,
          ourValuePence: 900,
          tharsternValuePence: 900,
          deltaPence: 0,
          standardsRow: "Guillotine setup + run, multi-up card cutting band",
        },
      ],
      "bc-2:250": [
        {
          // MR + run split, plus the plates line under Origination — this
          // press had one bundled "printing" line until coordinator review
          // round 5.
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOMR",
          quantityDriver: "1 make-ready, litho 4-colour",
          ourTimeMinutes: 3,
          tharsternTimeMinutes: 3,
          ourValuePence: 400,
          tharsternValuePence: 400,
          deltaPence: 0,
          standardsRow: "Litho make-ready band, L1",
        },
        {
          costCentreId: 800,
          costCentreCode: ccCode(800),
          costHead: "origination",
          opCode: "PLATEMAKING",
          quantityDriver: "8 plates (4/4, 1 section)",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 800,
          tharsternValuePence: 800,
          deltaPence: 0,
          standardsRow:
            "Plate cost × plate count, one plate per colour per section",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOPRINTRUN",
          quantityDriver: "18 sheets (SRA3, 14-up)",
          ourTimeMinutes: 1,
          tharsternTimeMinutes: 1,
          ourValuePence: 100,
          tharsternValuePence: 100,
          deltaPence: 0,
          standardsRow: "Run speed band L1 × sheet count, litho 4-colour",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "paper",
          opCode: "STOCKISSUE",
          // What we sent vs the stock Tharstern substituted (BC_2_SUBSTITUTION).
          quantityDriver: "18 sheets, WWW_UNC_400",
          tharsternQuantityDriver: "18 sheets, WWW_UNC_400_ALT",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 700,
          tharsternValuePence: 790,
          deltaPence: -90,
          standardsRow: "Stock issue, 400gsm uncoated board × sheet count",
        },
        {
          costCentreId: 814,
          costCentreCode: ccCode(814),
          costHead: "finishing",
          opCode: "LAMINATE",
          quantityDriver: "250 cards, soft-touch lam",
          ourTimeMinutes: 4,
          tharsternTimeMinutes: 4,
          ourValuePence: 1200,
          tharsternValuePence: 1200,
          deltaPence: 0,
          standardsRow: "Lamination run-speed band, soft-touch × sheet count",
        },
        {
          costCentreId: 801,
          costCentreCode: ccCode(801),
          costHead: "finishing",
          opCode: "GUILLOTINETRIM",
          quantityDriver: "4 cutting stacks, 250 cards",
          ourTimeMinutes: 3,
          tharsternTimeMinutes: 3,
          ourValuePence: 900,
          tharsternValuePence: 900,
          deltaPence: 0,
          standardsRow: "Guillotine setup + run, multi-up card cutting band",
        },
      ],
      "bc-2:500": [
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOMR",
          quantityDriver: "1 make-ready, litho 4-colour",
          ourTimeMinutes: 3,
          tharsternTimeMinutes: 3,
          ourValuePence: 400,
          tharsternValuePence: 400,
          deltaPence: 0,
          standardsRow: "Litho make-ready band, L1",
        },
        {
          costCentreId: 800,
          costCentreCode: ccCode(800),
          costHead: "origination",
          opCode: "PLATEMAKING",
          quantityDriver: "8 plates (4/4, 1 section)",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 800,
          tharsternValuePence: 800,
          deltaPence: 0,
          standardsRow:
            "Plate cost × plate count, one plate per colour per section",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOPRINTRUN",
          quantityDriver: "36 sheets (SRA3, 14-up)",
          ourTimeMinutes: 3,
          tharsternTimeMinutes: 3,
          ourValuePence: 1400,
          tharsternValuePence: 1400,
          deltaPence: 0,
          standardsRow: "Run speed band L1 × sheet count, litho 4-colour",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "paper",
          opCode: "STOCKISSUE",
          // What we sent vs the stock Tharstern substituted (BC_2_SUBSTITUTION).
          quantityDriver: "36 sheets, WWW_UNC_400",
          tharsternQuantityDriver: "36 sheets, WWW_UNC_400_ALT",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 1500,
          tharsternValuePence: 1690,
          deltaPence: -190,
          standardsRow: "Stock issue, 400gsm uncoated board × sheet count",
        },
        {
          costCentreId: 814,
          costCentreCode: ccCode(814),
          costHead: "finishing",
          opCode: "LAMINATE",
          quantityDriver: "500 cards, soft-touch lam",
          ourTimeMinutes: 7,
          tharsternTimeMinutes: 7,
          ourValuePence: 1400,
          tharsternValuePence: 1400,
          deltaPence: 0,
          standardsRow: "Lamination run-speed band, soft-touch × sheet count",
        },
        {
          costCentreId: 801,
          costCentreCode: ccCode(801),
          costHead: "finishing",
          opCode: "GUILLOTINETRIM",
          quantityDriver: "4 cutting stacks, 500 cards",
          ourTimeMinutes: 6,
          tharsternTimeMinutes: 6,
          ourValuePence: 700,
          tharsternValuePence: 700,
          deltaPence: 0,
          standardsRow: "Guillotine setup + run, multi-up card cutting band",
        },
      ],
      "bc-2:1000": [
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOMR",
          quantityDriver: "1 make-ready, litho 4-colour",
          ourTimeMinutes: 3,
          tharsternTimeMinutes: 3,
          ourValuePence: 400,
          tharsternValuePence: 400,
          deltaPence: 0,
          standardsRow: "Litho make-ready band, L1",
        },
        {
          costCentreId: 800,
          costCentreCode: ccCode(800),
          costHead: "origination",
          opCode: "PLATEMAKING",
          quantityDriver: "8 plates (4/4, 1 section)",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 800,
          tharsternValuePence: 800,
          deltaPence: 0,
          standardsRow:
            "Plate cost × plate count, one plate per colour per section",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "printing",
          opCode: "LITHOPRINTRUN",
          quantityDriver: "72 sheets (SRA3, 14-up)",
          ourTimeMinutes: 9,
          tharsternTimeMinutes: 9,
          ourValuePence: 3400,
          tharsternValuePence: 3400,
          deltaPence: 0,
          standardsRow: "Run speed band L1 × sheet count, litho 4-colour",
        },
        {
          costCentreId: 803,
          costCentreCode: ccCode(803),
          costHead: "paper",
          opCode: "STOCKISSUE",
          // What we sent vs the stock Tharstern substituted (BC_2_SUBSTITUTION).
          quantityDriver: "72 sheets, WWW_UNC_400",
          tharsternQuantityDriver: "72 sheets, WWW_UNC_400_ALT",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 2400,
          tharsternValuePence: 2700,
          deltaPence: -300,
          standardsRow: "Stock issue, 400gsm uncoated board × sheet count",
        },
        {
          costCentreId: 814,
          costCentreCode: ccCode(814),
          costHead: "finishing",
          opCode: "LAMINATE",
          quantityDriver: "1000 cards, soft-touch lam",
          ourTimeMinutes: 11,
          tharsternTimeMinutes: 11,
          ourValuePence: 1800,
          tharsternValuePence: 1800,
          deltaPence: 0,
          standardsRow: "Lamination run-speed band, soft-touch × sheet count",
        },
        {
          costCentreId: 801,
          costCentreCode: ccCode(801),
          costHead: "finishing",
          opCode: "GUILLOTINETRIM",
          quantityDriver: "8 cutting stacks, 1000 cards",
          ourTimeMinutes: 10,
          tharsternTimeMinutes: 10,
          ourValuePence: 1000,
          tharsternValuePence: 1000,
          deltaPence: 0,
          standardsRow: "Guillotine setup + run, multi-up card cutting band",
        },
      ],
    },
    costCentreReasons: {
      803: costCentreReason(
        "cheapest_suitable",
        { suitableCount: 3 },
        "Card · 350 gsm silk · 4/4",
      ),
      // Guillotine trim is feasible at both sites (Routing policy's own
      // per-step feasible-sites table), so this is the step map's default
      // choice, not a site rule.
      801: costCentreReason("step_map_default"),
      // Lamination (bc-2's soft-touch finish) — only one suitable press in
      // this part's candidate list.
      814: costCentreReason("only_suitable", { kind: "cost_centre" }),
      // Plate-making follows the chosen press's own site — only one
      // suitable plate room on that site.
      800: costCentreReason("only_suitable", { kind: "cost_centre" }),
    },
    // The mandated worked example (design review, 02_glossary_and_live_costing.md):
    // a tie between 803 and 5351 at £37.00, broken by the Exeter site rule.
    pressesConsidered: [
      {
        ...pressRef(803),
        site: "Exeter",
        outcome: "chosen",
        reason:
          "cheapest of 3 suitable presses; tied with 5351 — Exeter site-coherence rule broke the tie",
        wholeRouteCostPence: 3700,
      },
      {
        ...pressRef(5351),
        site: "Exeter",
        outcome: "suitable_not_chosen",
        reason: "tied on cost with 803 — the tie-break rule favoured 803",
        wholeRouteCostPence: 3700,
      },
      {
        ...pressRef(5830),
        site: "Buckingham",
        outcome: "suitable_not_chosen",
        reason: "suitable but not the cheapest",
        wholeRouteCostPence: 4120,
      },
      {
        ...pressRef(1117),
        site: "Exeter",
        outcome: "excluded",
        reason: "not in the part's press list",
        wholeRouteCostPence: null,
      },
      {
        ...pressRef(5627),
        site: "Exeter",
        outcome: "excluded",
        reason: "sheet below minimum feed",
        wholeRouteCostPence: null,
      },
    ],
    specTemplates: {
      "bc-1:250": [
        {
          label: "Product type",
          weSubmitted: "Business cards (PT63)",
          tharsternCosted: "Business cards (PT63)",
          differs: false,
        },
        {
          label: "Finished size",
          weSubmitted: "85×55mm",
          tharsternCosted: "85×55mm",
          differs: false,
        },
        {
          label: "Quantity",
          weSubmitted: "250",
          tharsternCosted: "250",
          differs: false,
        },
        // Same genuine stock discrepancy as bc-1:500 — it's a per-variant
        // record issue in Tharstern, not tied to one quantity.
        {
          label: "Stock",
          weSubmitted: "350 gsm silk",
          tharsternCosted: "370 gsm silk",
          differs: true,
        },
        {
          label: "Colours",
          weSubmitted: "4/4",
          tharsternCosted: "4/4",
          differs: false,
        },
        {
          label: "Finishing",
          weSubmitted: "Guillotine trim",
          tharsternCosted: "Guillotine trim",
          differs: false,
        },
      ],
      "bc-1:500": [
        {
          label: "Product type",
          weSubmitted: "Business cards (PT63)",
          tharsternCosted: "Business cards (PT63)",
          differs: false,
        },
        {
          label: "Finished size",
          weSubmitted: "85×55mm",
          tharsternCosted: "85×55mm",
          differs: false,
        },
        {
          label: "Quantity",
          weSubmitted: "500",
          tharsternCosted: "500",
          differs: false,
        },
        // A genuine difference — Tharstern's estimate recorded a heavier
        // stock than the one this variant actually specifies.
        {
          label: "Stock",
          weSubmitted: "350 gsm silk",
          tharsternCosted: "370 gsm silk",
          differs: true,
        },
        {
          label: "Colours",
          weSubmitted: "4/4",
          tharsternCosted: "4/4",
          differs: false,
        },
        {
          label: "Finishing",
          weSubmitted: "Guillotine trim",
          tharsternCosted: "Guillotine trim",
          differs: false,
        },
      ],
      "bc-1:1000": [
        {
          label: "Product type",
          weSubmitted: "Business cards (PT63)",
          tharsternCosted: "Business cards (PT63)",
          differs: false,
        },
        {
          label: "Finished size",
          weSubmitted: "85×55mm",
          tharsternCosted: "85×55mm",
          differs: false,
        },
        {
          label: "Quantity",
          weSubmitted: "1000",
          tharsternCosted: "1000",
          differs: false,
        },
        {
          label: "Stock",
          weSubmitted: "350 gsm silk",
          tharsternCosted: "370 gsm silk",
          differs: true,
        },
        {
          label: "Colours",
          weSubmitted: "4/4",
          tharsternCosted: "4/4",
          differs: false,
        },
        {
          label: "Finishing",
          weSubmitted: "Guillotine trim",
          tharsternCosted: "Guillotine trim",
          differs: false,
        },
      ],
      "bc-2:250": [
        {
          label: "Product type",
          weSubmitted: "Business cards (PT63)",
          tharsternCosted: "Business cards (PT63)",
          differs: false,
        },
        {
          label: "Finished size",
          weSubmitted: "85×55mm",
          tharsternCosted: "85×55mm",
          differs: false,
        },
        {
          label: "Quantity",
          weSubmitted: "250",
          tharsternCosted: "250",
          differs: false,
        },
        {
          label: "Stock",
          weSubmitted: "400 gsm uncoated (WWW_UNC_400)",
          tharsternCosted: "WWW_UNC_400_ALT",
          differs: true,
        },
        {
          label: "Colours",
          weSubmitted: "4/4",
          tharsternCosted: "4/4",
          differs: false,
        },
        {
          label: "Finishing",
          weSubmitted: "Guillotine trim, soft-touch lamination",
          tharsternCosted: "Guillotine trim, soft-touch lamination",
          differs: false,
        },
      ],
      "bc-2:500": [
        {
          label: "Product type",
          weSubmitted: "Business cards (PT63)",
          tharsternCosted: "Business cards (PT63)",
          differs: false,
        },
        {
          label: "Finished size",
          weSubmitted: "85×55mm",
          tharsternCosted: "85×55mm",
          differs: false,
        },
        {
          label: "Quantity",
          weSubmitted: "500",
          tharsternCosted: "500",
          differs: false,
        },
        {
          label: "Stock",
          weSubmitted: "400 gsm uncoated (WWW_UNC_400)",
          tharsternCosted: "WWW_UNC_400_ALT",
          differs: true,
        },
        {
          label: "Colours",
          weSubmitted: "4/4",
          tharsternCosted: "4/4",
          differs: false,
        },
        {
          label: "Finishing",
          weSubmitted: "Guillotine trim, soft-touch lamination",
          tharsternCosted: "Guillotine trim, soft-touch lamination",
          differs: false,
        },
      ],
      "bc-2:1000": [
        {
          label: "Product type",
          weSubmitted: "Business cards (PT63)",
          tharsternCosted: "Business cards (PT63)",
          differs: false,
        },
        {
          label: "Finished size",
          weSubmitted: "85×55mm",
          tharsternCosted: "85×55mm",
          differs: false,
        },
        {
          label: "Quantity",
          weSubmitted: "1000",
          tharsternCosted: "1000",
          differs: false,
        },
        {
          label: "Stock",
          weSubmitted: "400 gsm uncoated (WWW_UNC_400)",
          tharsternCosted: "WWW_UNC_400_ALT",
          differs: true,
        },
        {
          label: "Colours",
          weSubmitted: "4/4",
          tharsternCosted: "4/4",
          differs: false,
        },
        {
          label: "Finishing",
          weSubmitted: "Guillotine trim, soft-touch lamination",
          tharsternCosted: "Guillotine trim, soft-touch lamination",
          differs: false,
        },
      ],
    },
  },

  // 4. Wide format panels — PT44/33 — not yet checked (hybrid press not priceable, hybrid reel never checked).
  {
    dictionaryItemId: "dict-wide-format-panels",
    name: "Wide format panels",
    productTypeId: 44,
    costCheckState: "grey",
    mismatchingCostLine: null,
    pennyExactPct: null,
    within5PctPct: null,
    lastCheckedAt: null,
    usedByCount: 2,
    checklist: [
      {
        key: "cost_lines_matching",
        label: `${COST_LINE_5607_RUN.label} and ${COST_LINE_5609_RUN.label} have not yet returned a check`,
        met: false,
      },
      {
        key: "routing_validated",
        label:
          "No orders yet — this item has not been checked, so press-choice agreement can't be evaluated",
        met: false,
      },
      {
        key: "no_grey_routes",
        label: `${COST_LINE_5607_RUN.label} (hybrid sheet) and ${COST_LINE_5609_RUN.label} (hybrid reel) have both not yet been checked`,
        met: false,
      },
      {
        key: "no_open_red",
        label:
          "No cost line on this item is currently mismatching — they simply have no evidence yet",
        met: true,
      },
    ],
    variants: [
      {
        variantId: "wfp-1",
        displayLabel: "Foamex panel 5mm, 594×841mm",
        cells: [
          {
            quantity: 1,
            costPence: null,
            state: "grey",
            costLine: COST_LINE_5607_RUN,
            reason:
              "RUN priced from a hybrid-press value only Tharstern computes",
          },
          {
            quantity: 5,
            costPence: null,
            state: "grey",
            costLine: COST_LINE_5607_RUN,
            reason:
              "RUN priced from a hybrid-press value only Tharstern computes",
          },
          {
            quantity: 10,
            costPence: null,
            state: "grey",
            costLine: COST_LINE_5609_RUN,
            reason: "Not yet checked — new to rolling validation this week",
          },
        ],
      },
      {
        variantId: "wfp-2",
        displayLabel: "Correx panel 4mm, 594×841mm",
        cells: [
          {
            quantity: 1,
            costPence: null,
            state: "grey",
            costLine: COST_LINE_5609_RUN,
            reason: "Not yet checked — new to rolling validation this week",
          },
          {
            quantity: 5,
            costPence: null,
            state: "grey",
            costLine: COST_LINE_5609_RUN,
            reason: "Not yet checked — new to rolling validation this week",
          },
          {
            quantity: 10,
            costPence: null,
            state: "grey",
            costLine: COST_LINE_5609_RUN,
            reason: "Not yet checked — new to rolling validation this week",
          },
        ],
      },
    ],
    lineTemplates: {},
    // No lines to compare (never priced from spec), so no cost centre has a
    // sub-header to attach a reason to — the Calculation table renders "Not
    // costed yet" instead.
    costCentreReasons: {},
    pressesConsidered: [
      {
        ...pressRef(5607),
        site: "Buckingham",
        outcome: "excluded",
        reason: "hybrid-press value not yet captured — routing withheld",
        wholeRouteCostPence: null,
      },
      {
        ...pressRef(5609),
        site: "Buckingham",
        outcome: "excluded",
        reason: "never validated",
        wholeRouteCostPence: null,
      },
    ],
    specTemplates: {},
  },

  // 5. Letterheads — PT77 — live, matches throughout.
  {
    dictionaryItemId: "dict-letterheads",
    name: "Letterheads",
    productTypeId: 77,
    costCheckState: "green",
    mismatchingCostLine: null,
    pennyExactPct: 76,
    within5PctPct: 99,
    lastCheckedAt: isoDaysAgo(0, 9, 0),
    usedByCount: 4,
    checklist: [
      {
        key: "cost_lines_matching",
        label: `${COST_LINE_5627_PRINT.label} and ${COST_LINE_835_FINISHING.label} have both matched Tharstern for the last 4 weeks`,
        met: true,
      },
      {
        key: "routing_validated",
        label: "Tharstern agrees with our press choice on 29 of 30 orders",
        met: true,
      },
      {
        key: "no_grey_routes",
        label:
          "Every cost line on this item's routes has been checked at least once",
        met: true,
      },
      {
        key: "no_open_red",
        label: "No cost line on this item is currently mismatching",
        met: true,
      },
    ],
    variants: [
      {
        variantId: "lh-1",
        displayLabel: "100gsm laid, 1-colour",
        cells: [
          {
            // Matches the "lh-1:250" line-item breakdown's total exactly
            // (3200 printing + 600 paper + 400 fold).
            quantity: 250,
            costPence: 4200,
            state: "green",
            origin: "scheduled",
          },
          {
            // Matches the "lh-1:500" line-item breakdown's total exactly
            // (4600 printing + 1000 paper + 500 fold).
            quantity: 500,
            costPence: 6100,
            state: "green",
          },
          {
            // Matches the "lh-1:1000" line-item breakdown's total exactly
            // (7000 printing + 1700 paper + 700 fold).
            quantity: 1000,
            costPence: 9400,
            state: "green",
          },
        ],
      },
      {
        variantId: "lh-2",
        displayLabel: "120gsm woven, full colour",
        cells: [
          {
            // Matches the "lh-2:250" line-item breakdown's total exactly
            // (4200 printing + 1400 paper + 1200 fold).
            quantity: 250,
            costPence: 6800,
            state: "green",
          },
          {
            // Matches the "lh-2:500" line-item breakdown's total exactly
            // (7200 printing + 2400 fold + 1800 paper — paper added in
            // coordinator review round 4).
            quantity: 500,
            costPence: 11400,
            state: "green",
          },
          {
            // Matches the "lh-2:1000" line-item breakdown's total exactly
            // (9200 printing + 3300 paper + 2400 fold).
            quantity: 1000,
            costPence: 14900,
            state: "green",
          },
        ],
      },
      {
        // A fixed-cost corner (spec 03 rule (d)): lh-1 with its PDF proof
        // switched on, in the rolling set at 250 only — the plain lh-1:250
        // total plus the proof's 952p job-level line.
        variantId: "lh-1",
        displayLabel: "100gsm laid, 1-colour",
        fixedCostOptions: PDF_PROOF_ON,
        cells: [
          {
            quantity: 250,
            costPence: 5152,
            state: "green",
            origin: "scheduled",
          },
          {
            quantity: 500,
            costPence: null,
            state: "grey",
            reason: "Not yet checked with these options",
          },
          {
            quantity: 1000,
            costPence: null,
            state: "grey",
            reason: "Not yet checked with these options",
          },
        ],
      },
    ],
    lineTemplates: {
      "lh-1:250": [
        {
          // MR + run split — digital presses skip Origination (no plates).
          costCentreId: 5627,
          costCentreCode: ccCode(5627),
          costHead: "printing",
          opCode: "DIGITALMR",
          quantityDriver: "1 make-ready, digital",
          ourTimeMinutes: 2,
          tharsternTimeMinutes: 2,
          ourValuePence: 300,
          tharsternValuePence: 300,
          deltaPence: 0,
          standardsRow: "Digital make-ready band, HP Indigo 12000",
        },
        {
          costCentreId: 5627,
          costCentreCode: ccCode(5627),
          costHead: "printing",
          opCode: "DIGITALPRINTRUN",
          quantityDriver: "250 sheets (SRA3)",
          ourTimeMinutes: 2,
          tharsternTimeMinutes: 2,
          ourValuePence: 2900,
          tharsternValuePence: 2900,
          deltaPence: 0,
          standardsRow: "Indigo click rate × sheet count",
        },
        {
          costCentreId: 5627,
          costCentreCode: ccCode(5627),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "250 sheets, 100gsm laid",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 600,
          tharsternValuePence: 600,
          deltaPence: 0,
          standardsRow: "Stock issue, 100gsm laid × sheet count",
        },
        {
          costCentreId: 835,
          costCentreCode: ccCode(835),
          costHead: "finishing",
          opCode: "SECTIONFOLD",
          quantityDriver: "250 sheets, single fold",
          ourTimeMinutes: 2,
          tharsternTimeMinutes: 2,
          ourValuePence: 400,
          tharsternValuePence: 400,
          deltaPence: 0,
          standardsRow: "KH82 fold run-speed band × sheet count",
        },
      ],
      "lh-1:500": [
        {
          costCentreId: 5627,
          costCentreCode: ccCode(5627),
          costHead: "printing",
          opCode: "DIGITALMR",
          quantityDriver: "1 make-ready, digital",
          ourTimeMinutes: 2,
          tharsternTimeMinutes: 2,
          ourValuePence: 300,
          tharsternValuePence: 300,
          deltaPence: 0,
          standardsRow: "Digital make-ready band, HP Indigo 12000",
        },
        {
          costCentreId: 5627,
          costCentreCode: ccCode(5627),
          costHead: "printing",
          opCode: "DIGITALPRINTRUN",
          quantityDriver: "500 sheets (SRA3)",
          ourTimeMinutes: 4,
          tharsternTimeMinutes: 4,
          ourValuePence: 4300,
          tharsternValuePence: 4300,
          deltaPence: 0,
          standardsRow: "Indigo click rate × sheet count",
        },
        {
          costCentreId: 5627,
          costCentreCode: ccCode(5627),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "500 sheets, 100gsm laid",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 1000,
          tharsternValuePence: 1000,
          deltaPence: 0,
          standardsRow: "Stock issue, 100gsm laid × sheet count",
        },
        {
          costCentreId: 835,
          costCentreCode: ccCode(835),
          costHead: "finishing",
          opCode: "SECTIONFOLD",
          quantityDriver: "500 sheets, single fold",
          ourTimeMinutes: 3,
          tharsternTimeMinutes: 3,
          ourValuePence: 500,
          tharsternValuePence: 500,
          deltaPence: 0,
          standardsRow: "KH82 fold run-speed band × sheet count",
        },
      ],
      "lh-1:1000": [
        {
          costCentreId: 5627,
          costCentreCode: ccCode(5627),
          costHead: "printing",
          opCode: "DIGITALMR",
          quantityDriver: "1 make-ready, digital",
          ourTimeMinutes: 2,
          tharsternTimeMinutes: 2,
          ourValuePence: 300,
          tharsternValuePence: 300,
          deltaPence: 0,
          standardsRow: "Digital make-ready band, HP Indigo 12000",
        },
        {
          costCentreId: 5627,
          costCentreCode: ccCode(5627),
          costHead: "printing",
          opCode: "DIGITALPRINTRUN",
          quantityDriver: "1,000 sheets (SRA3)",
          ourTimeMinutes: 9,
          tharsternTimeMinutes: 9,
          ourValuePence: 6700,
          tharsternValuePence: 6700,
          deltaPence: 0,
          standardsRow: "Indigo click rate × sheet count",
        },
        {
          costCentreId: 5627,
          costCentreCode: ccCode(5627),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "1,000 sheets, 100gsm laid",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 1700,
          tharsternValuePence: 1700,
          deltaPence: 0,
          standardsRow: "Stock issue, 100gsm laid × sheet count",
        },
        {
          costCentreId: 835,
          costCentreCode: ccCode(835),
          costHead: "finishing",
          opCode: "SECTIONFOLD",
          quantityDriver: "1,000 sheets, single fold",
          ourTimeMinutes: 5,
          tharsternTimeMinutes: 5,
          ourValuePence: 700,
          tharsternValuePence: 700,
          deltaPence: 0,
          standardsRow: "KH82 fold run-speed band × sheet count",
        },
      ],
      "lh-2:250": [
        {
          costCentreId: 5627,
          costCentreCode: ccCode(5627),
          costHead: "printing",
          opCode: "DIGITALMR",
          quantityDriver: "1 make-ready, digital",
          ourTimeMinutes: 2,
          tharsternTimeMinutes: 2,
          ourValuePence: 300,
          tharsternValuePence: 300,
          deltaPence: 0,
          standardsRow: "Digital make-ready band, HP Indigo 12000",
        },
        {
          costCentreId: 5627,
          costCentreCode: ccCode(5627),
          costHead: "printing",
          opCode: "DIGITALPRINTRUN",
          quantityDriver: "250 sheets (SRA3)",
          ourTimeMinutes: 2,
          tharsternTimeMinutes: 2,
          ourValuePence: 3900,
          tharsternValuePence: 3900,
          deltaPence: 0,
          standardsRow: "Indigo click rate × sheet count",
        },
        {
          costCentreId: 5627,
          costCentreCode: ccCode(5627),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "250 sheets, 120gsm woven",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 1400,
          tharsternValuePence: 1400,
          deltaPence: 0,
          standardsRow: "Stock issue, 120gsm woven × sheet count",
        },
        {
          costCentreId: 835,
          costCentreCode: ccCode(835),
          costHead: "finishing",
          opCode: "SECTIONFOLD",
          quantityDriver: "250 sheets, single fold",
          ourTimeMinutes: 2,
          tharsternTimeMinutes: 2,
          ourValuePence: 1200,
          tharsternValuePence: 1200,
          deltaPence: 0,
          standardsRow: "KH82 fold run-speed band × sheet count",
        },
      ],
      "lh-2:500": [
        {
          costCentreId: 5627,
          costCentreCode: ccCode(5627),
          costHead: "printing",
          opCode: "DIGITALMR",
          quantityDriver: "1 make-ready, digital",
          ourTimeMinutes: 2,
          tharsternTimeMinutes: 2,
          ourValuePence: 300,
          tharsternValuePence: 300,
          deltaPence: 0,
          standardsRow: "Digital make-ready band, HP Indigo 12000",
        },
        {
          costCentreId: 5627,
          costCentreCode: ccCode(5627),
          costHead: "printing",
          opCode: "DIGITALPRINTRUN",
          quantityDriver: "500 sheets (SRA3)",
          ourTimeMinutes: 5,
          tharsternTimeMinutes: 5,
          ourValuePence: 6900,
          tharsternValuePence: 6900,
          deltaPence: 0,
          standardsRow: "Indigo click rate × sheet count",
        },
        {
          costCentreId: 835,
          costCentreCode: ccCode(835),
          costHead: "finishing",
          opCode: "SECTIONFOLD",
          quantityDriver: "500 sheets, single fold",
          ourTimeMinutes: 3,
          tharsternTimeMinutes: 3,
          ourValuePence: 2400,
          tharsternValuePence: 2400,
          deltaPence: 0,
          standardsRow: "KH82 fold run-speed band × sheet count",
        },
        {
          // Paper line — this pair had none until coordinator review round 4.
          costCentreId: 5627,
          costCentreCode: ccCode(5627),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "500 sheets, 120gsm woven",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 1800,
          tharsternValuePence: 1800,
          deltaPence: 0,
          standardsRow: "Stock issue, 120gsm woven × sheet count",
        },
      ],
      "lh-2:1000": [
        {
          costCentreId: 5627,
          costCentreCode: ccCode(5627),
          costHead: "printing",
          opCode: "DIGITALMR",
          quantityDriver: "1 make-ready, digital",
          ourTimeMinutes: 2,
          tharsternTimeMinutes: 2,
          ourValuePence: 300,
          tharsternValuePence: 300,
          deltaPence: 0,
          standardsRow: "Digital make-ready band, HP Indigo 12000",
        },
        {
          costCentreId: 5627,
          costCentreCode: ccCode(5627),
          costHead: "printing",
          opCode: "DIGITALPRINTRUN",
          quantityDriver: "1,000 sheets (SRA3)",
          ourTimeMinutes: 9,
          tharsternTimeMinutes: 9,
          ourValuePence: 8900,
          tharsternValuePence: 8900,
          deltaPence: 0,
          standardsRow: "Indigo click rate × sheet count",
        },
        {
          costCentreId: 5627,
          costCentreCode: ccCode(5627),
          costHead: "paper",
          opCode: "STOCKISSUE",
          quantityDriver: "1,000 sheets, 120gsm woven",
          ourTimeMinutes: null,
          tharsternTimeMinutes: null,
          ourValuePence: 3300,
          tharsternValuePence: 3300,
          deltaPence: 0,
          standardsRow: "Stock issue, 120gsm woven × sheet count",
        },
        {
          costCentreId: 835,
          costCentreCode: ccCode(835),
          costHead: "finishing",
          opCode: "SECTIONFOLD",
          quantityDriver: "1,000 sheets, single fold",
          ourTimeMinutes: 5,
          tharsternTimeMinutes: 5,
          ourValuePence: 2400,
          tharsternValuePence: 2400,
          deltaPence: 0,
          standardsRow: "KH82 fold run-speed band × sheet count",
        },
      ],
    },
    costCentreReasons: {
      5627: costCentreReason(
        "indigo_option",
        {},
        "Sheet · 120 gsm woven · 4/0",
      ),
      835: costCentreReason("step_map_default"),
    },
    pressesConsidered: [
      {
        ...pressRef(5627),
        site: "Exeter",
        outcome: "chosen",
        reason:
          "only suitable press — Indigo flag resolved on this dictionary item",
        wholeRouteCostPence: 9600,
      },
    ],
    specTemplates: {
      "lh-1:250": [
        {
          label: "Product type",
          weSubmitted: "Letterheads (PT77)",
          tharsternCosted: "Letterheads (PT77)",
          differs: false,
        },
        {
          label: "Finished size",
          weSubmitted: "A4",
          tharsternCosted: "A4",
          differs: false,
        },
        {
          label: "Quantity",
          weSubmitted: "250",
          tharsternCosted: "250",
          differs: false,
        },
        {
          label: "Stock",
          weSubmitted: "100 gsm laid",
          tharsternCosted: "100 gsm laid",
          differs: false,
        },
        {
          label: "Colours",
          weSubmitted: "1/0",
          tharsternCosted: "1/0",
          differs: false,
        },
        {
          label: "Finishing",
          weSubmitted: "Single fold",
          tharsternCosted: "Single fold",
          differs: false,
        },
      ],
      "lh-1:500": [
        {
          label: "Product type",
          weSubmitted: "Letterheads (PT77)",
          tharsternCosted: "Letterheads (PT77)",
          differs: false,
        },
        {
          label: "Finished size",
          weSubmitted: "A4",
          tharsternCosted: "A4",
          differs: false,
        },
        {
          label: "Quantity",
          weSubmitted: "500",
          tharsternCosted: "500",
          differs: false,
        },
        {
          label: "Stock",
          weSubmitted: "100 gsm laid",
          tharsternCosted: "100 gsm laid",
          differs: false,
        },
        {
          label: "Colours",
          weSubmitted: "1/0",
          tharsternCosted: "1/0",
          differs: false,
        },
        {
          label: "Finishing",
          weSubmitted: "Single fold",
          tharsternCosted: "Single fold",
          differs: false,
        },
      ],
      "lh-1:1000": [
        {
          label: "Product type",
          weSubmitted: "Letterheads (PT77)",
          tharsternCosted: "Letterheads (PT77)",
          differs: false,
        },
        {
          label: "Finished size",
          weSubmitted: "A4",
          tharsternCosted: "A4",
          differs: false,
        },
        {
          label: "Quantity",
          weSubmitted: "1000",
          tharsternCosted: "1000",
          differs: false,
        },
        {
          label: "Stock",
          weSubmitted: "100 gsm laid",
          tharsternCosted: "100 gsm laid",
          differs: false,
        },
        {
          label: "Colours",
          weSubmitted: "1/0",
          tharsternCosted: "1/0",
          differs: false,
        },
        {
          label: "Finishing",
          weSubmitted: "Single fold",
          tharsternCosted: "Single fold",
          differs: false,
        },
      ],
      "lh-2:250": [
        {
          label: "Product type",
          weSubmitted: "Letterheads (PT77)",
          tharsternCosted: "Letterheads (PT77)",
          differs: false,
        },
        {
          label: "Finished size",
          weSubmitted: "A4",
          tharsternCosted: "A4",
          differs: false,
        },
        {
          label: "Quantity",
          weSubmitted: "250",
          tharsternCosted: "250",
          differs: false,
        },
        {
          label: "Stock",
          weSubmitted: "120 gsm woven",
          tharsternCosted: "100 gsm woven",
          differs: true,
        },
        {
          label: "Colours",
          weSubmitted: "4/0",
          tharsternCosted: "4/0",
          differs: false,
        },
        {
          label: "Finishing",
          weSubmitted: "Single fold",
          tharsternCosted: "Single fold",
          differs: false,
        },
      ],
      "lh-2:500": [
        {
          label: "Product type",
          weSubmitted: "Letterheads (PT77)",
          tharsternCosted: "Letterheads (PT77)",
          differs: false,
        },
        {
          label: "Finished size",
          weSubmitted: "A4",
          tharsternCosted: "A4",
          differs: false,
        },
        {
          label: "Quantity",
          weSubmitted: "500",
          tharsternCosted: "500",
          differs: false,
        },
        // A second genuine difference (coordinator review round 2): a
        // paper substitution that happens not to move the price much.
        {
          label: "Stock",
          weSubmitted: "120 gsm woven",
          tharsternCosted: "100 gsm woven",
          differs: true,
        },
        {
          label: "Colours",
          weSubmitted: "4/0",
          tharsternCosted: "4/0",
          differs: false,
        },
        {
          label: "Finishing",
          weSubmitted: "Single fold",
          tharsternCosted: "Single fold",
          differs: false,
        },
      ],
      "lh-2:1000": [
        {
          label: "Product type",
          weSubmitted: "Letterheads (PT77)",
          tharsternCosted: "Letterheads (PT77)",
          differs: false,
        },
        {
          label: "Finished size",
          weSubmitted: "A4",
          tharsternCosted: "A4",
          differs: false,
        },
        {
          label: "Quantity",
          weSubmitted: "1000",
          tharsternCosted: "1000",
          differs: false,
        },
        {
          label: "Stock",
          weSubmitted: "120 gsm woven",
          tharsternCosted: "100 gsm woven",
          differs: true,
        },
        {
          label: "Colours",
          weSubmitted: "4/0",
          tharsternCosted: "4/0",
          differs: false,
        },
        {
          label: "Finishing",
          weSubmitted: "Single fold",
          tharsternCosted: "Single fold",
          differs: false,
        },
      ],
    },
  },

  // 6. Greeting cards — PT91 — a 40+-variant item, to exercise the item
  // page's variant-matrix search/filter/paging (design review,
  // 02_glossary_and_live_costing.md). Mostly matching, generated rather than
  // hand-listed — 46 variants × 2 quantities, a handful pinned.
  buildManyVariantsFixture(),

  // 7. Promotional postcards — PT22 — green, no cost-line mismatch.
  // Minimal — this fixture is read by the estate/items list only, never
  // opened as a detail page, so the matrix/pair/checklist fields are empty.
  {
    dictionaryItemId: "dict-promotional-postcards",
    name: "Promotional postcards",
    productTypeId: 22,
    costCheckState: "green",
    mismatchingCostLine: null,
    pennyExactPct: 88,
    within5PctPct: 100,
    lastCheckedAt: isoDaysAgo(1, 9, 15),
    usedByCount: 1,
    checklist: [],
    variants: [],
    lineTemplates: {},
    costCentreReasons: {},
    pressesConsidered: [],
    specTemplates: {},
  },
];

/** Generates the 40+-variant "Greeting cards" fixture — one variant per
 *  card size/stock combination, each with two quantity columns, almost all
 *  matching. Kept generated rather than hand-listed (46 near-identical
 *  variants would be pure noise to hand-author and review). */
function buildManyVariantsFixture(): DictionaryValidationFixture {
  const sizes = ["A6", "A5", "5x7in", "Square 148mm", "DL"];
  const stocks = [
    "300gsm silk",
    "300gsm uncoated",
    "350gsm recycled",
    "270gsm gloss lam",
    "270gsm matt lam",
    "320gsm textured",
    "300gsm pearlescent",
    "280gsm foil-block",
    "300gsm silk soft-touch",
  ];
  const variants: VariantSpec[] = [];
  let index = 0;
  for (const size of sizes) {
    for (const stock of stocks) {
      index += 1;
      const isSmallDifference = index === 7 || index === 23;
      variants.push({
        variantId: `gc-${index}`,
        displayLabel: `${size}, ${stock}`,
        cells: [
          {
            quantity: 100,
            costPence: 2200 + index * 15,
            state: "green",
            origin: index === 1 || index === 20 ? "scheduled" : undefined,
          },
          {
            quantity: 250,
            costPence: 3400 + index * 22,
            state: isSmallDifference ? "amber" : "green",
            tharsternPence: isSmallDifference
              ? 3400 + index * 22 + 60
              : undefined,
            costLine: isSmallDifference ? COST_LINE_5627_PRINT : undefined,
            reason: isSmallDifference
              ? "Indigo click rate ~2% under standards — small, non-systematic"
              : undefined,
          },
        ],
      });
    }
  }
  return {
    dictionaryItemId: "dict-greeting-cards",
    name: "Greeting cards",
    productTypeId: 91,
    costCheckState: "amber",
    mismatchingCostLine: null,
    pennyExactPct: 88,
    within5PctPct: 100,
    lastCheckedAt: isoDaysAgo(0, 10, 0),
    usedByCount: 3,
    checklist: [
      {
        key: "cost_lines_matching",
        label: `${COST_LINE_5627_PRINT.label} has a small, non-systematic difference from Tharstern — doesn't block live costing`,
        met: true,
      },
      {
        key: "routing_validated",
        label: "Tharstern agrees with our press choice on 58 of 60 orders",
        met: true,
      },
      {
        key: "no_grey_routes",
        label:
          "Every cost line on this item's routes has been checked at least once",
        met: true,
      },
      {
        key: "no_open_red",
        label: "No cost line on this item is currently mismatching",
        met: true,
      },
    ],
    variants,
    lineTemplates: {
      "gc-1:100": [
        {
          costCentreId: 5627,
          costCentreCode: ccCode(5627),
          costHead: "printing",
          opCode: "DIGITALMR",
          quantityDriver: "1 make-ready, digital",
          ourTimeMinutes: 1,
          tharsternTimeMinutes: 1,
          ourValuePence: 150,
          tharsternValuePence: 150,
          deltaPence: 0,
          standardsRow: "Digital make-ready band, HP Indigo 12000",
        },
        {
          costCentreId: 5627,
          costCentreCode: ccCode(5627),
          costHead: "printing",
          opCode: "DIGITALPRINTRUN",
          quantityDriver: "100 sheets (SRA4)",
          ourTimeMinutes: 1,
          tharsternTimeMinutes: 1,
          ourValuePence: 2065,
          tharsternValuePence: 2065,
          deltaPence: 0,
          standardsRow: "Indigo click rate × sheet count",
        },
      ],
    },
    costCentreReasons: {
      5627: costCentreReason("indigo_option", {}, "Card · 300 gsm silk · 4/4"),
    },
    pressesConsidered: [
      {
        ...pressRef(5627),
        site: "Exeter",
        outcome: "chosen",
        reason:
          "only suitable press — Indigo flag resolved on this dictionary item",
        wholeRouteCostPence: 2215,
      },
    ],
    specTemplates: {
      "gc-1:100": [
        {
          label: "Product type",
          weSubmitted: "Greeting cards (PT91)",
          tharsternCosted: "Greeting cards (PT91)",
          differs: false,
        },
        {
          label: "Finished size",
          weSubmitted: "A6",
          tharsternCosted: "A6",
          differs: false,
        },
        {
          label: "Quantity",
          weSubmitted: "100",
          tharsternCosted: "100",
          differs: false,
        },
        {
          label: "Stock",
          weSubmitted: "300 gsm silk",
          tharsternCosted: "300 gsm silk",
          differs: false,
        },
        {
          label: "Colours",
          weSubmitted: "4/4",
          tharsternCosted: "4/4",
          differs: false,
        },
      ],
    },
  };
}

const fixturesById = new Map(FIXTURES.map((f) => [f.dictionaryItemId, f]));

/** Rolling prices quarantined after a Tharstern mint timeout (spec 03 §5.1
 *  step 4c), by item. One on the perfect bound book — the first of a run of
 *  timeouts, so the likely culprit — keeps the panel's quarantined state and
 *  the boards' counts in every mock-backed page and story. */
const quarantinedByItem = new Map<string, QuarantinedPrice[]>([
  [
    "dict-perfect-bound-book",
    [
      {
        type: "rolling_price",
        releaseUrl: "/cost-checks/prices/rp-pbb-2-250/release-quarantine",
        rollingPriceId: "rp-pbb-2-250",
        variantId: "pbb-2",
        variantLabel: "A4 128pp, 250gsm cover gloss lam, 100gsm text",
        quantity: 250,
        fixedCostOptions: [],
        quarantinedAt: isoDaysAgo(0, 9, 17),
        quarantineReason:
          "Tharstern timed out minting this price (rolling run, tak_call_log 88213).",
      },
    ],
  ],
]);

function quarantinedPricesFor(dictionaryItemId: string): QuarantinedPrice[] {
  return [...(quarantinedByItem.get(dictionaryItemId) ?? [])];
}

/** Release one quarantined price (the note is required, as the backend's
 *  `note_required` field error insists) and return the refreshed detail. */
export function releaseMockQuarantinedPrice(
  dictionaryItemId: string,
  releaseUrl: string,
  note: string,
): LiveCostingDetail {
  if (!note.trim()) {
    throw new Error("Say why this price can be minted again.");
  }
  quarantinedByItem.set(
    dictionaryItemId,
    quarantinedPricesFor(dictionaryItemId).filter(
      (price) => price.releaseUrl !== releaseUrl,
    ),
  );
  return getMockDictionaryLiveCostingDetail(dictionaryItemId);
}

// lh-1 with its PDF proof switched on (the letterheads' fixed-cost corner):
// exactly the plain lh-1:250 lines plus the proof's own once-per-job line,
// 952p on PRE-PDF — the engine's figure for EX-PRE-PROOF-PDF. Keyed with the
// options, so the plain pair never shows it.
{
  const letterheads = fixturesById.get("dict-letterheads");
  if (letterheads) {
    const cornerKey = `lh-1:250:${fixedCostOptionsKey(PDF_PROOF_ON)}`;
    letterheads.lineTemplates[cornerKey] = [
      ...(letterheads.lineTemplates["lh-1:250"] ?? []),
      {
        costCentreId: 896,
        costCentreCode: ccCode(896),
        costHead: "origination",
        opCode: "EX-PRE-PROOF-PDF",
        quantityDriver: "1 PDF proof per job",
        ourTimeMinutes: 20,
        tharsternTimeMinutes: 20,
        ourValuePence: 952,
        tharsternValuePence: 952,
        deltaPence: 0,
        standardsRow: "PDF proofing, once per job",
      },
    ];
    letterheads.specTemplates[cornerKey] = [
      ...(letterheads.specTemplates["lh-1:250"] ?? []),
      {
        label: "Proofing",
        weSubmitted: "PDF proof",
        tharsternCosted: "PDF proof",
        differs: false,
      },
    ];
  }
}

// ── Reads ────────────────────────────────────────────────────────────────

/** Ours/Tharstern from whichever cell was checked most recently — the
 *  cost-check badge's tooltip triplet on the "by dictionary item" index.
 *  `null` when nothing on the item has ever been checked. */
function lastCheckForFixture(
  f: DictionaryValidationFixture,
): { oursPence: number; tharsternPence: number } | null {
  const checked = f.variants
    .flatMap((v) => cellsFor(v))
    .filter(
      (
        c,
      ): c is ValidationMatrixCell & {
        lastCheckedAt: string;
        costPence: number;
        tharsternPence: number;
      } =>
        c.lastCheckedAt != null &&
        c.costPence != null &&
        c.tharsternPence != null,
    )
    .sort((a, b) => b.lastCheckedAt.localeCompare(a.lastCheckedAt));
  if (checked.length === 0) return null;
  const latest = checked[0];
  return { oursPence: latest.costPence, tharsternPence: latest.tharsternPence };
}

// Tharstern's own product-type names for every product type these fixtures
// use — mirrors `manufacturing-setup.ts`'s `PRODUCT_TYPES` names exactly
// (same "Read it as Henry" consistency rule as `COST_CENTRE_SITE` above) so
// the dictionary index and the Live costing items table never disagree with
// Manufacturing setup about what a product type is called.
const PRODUCT_TYPE_NAMES: Record<number, string> = {
  1: "Brochure Cover & Text",
  36: "Perfect Bound Book",
  44: "Wide format panels",
  63: "Business cards",
  77: "Letterheads",
  91: "Greeting cards",
};

function dictionaryItemSelf(dictionaryItemId: string): string {
  return `/dictionary/admin/${dictionaryItemId}`;
}

function liveCostingMatrixUrl(dictionaryItemId: string): string {
  return `/views/live-costing/items/${dictionaryItemId}/matrix`;
}

function liveCostingChecksUrlForProductType(productTypeId: number): string {
  return `/views/live-costing/cost-lines?product_type_id=${productTypeId}`;
}

function indexEntryFromFixture(
  f: DictionaryValidationFixture,
): DictionaryValidationIndexEntry {
  return {
    _self: dictionaryItemSelf(f.dictionaryItemId),
    type: "dictionary_item",
    checksUrl: liveCostingChecksUrlForProductType(f.productTypeId),
    matrixUrl: liveCostingMatrixUrl(f.dictionaryItemId),
    dictionaryItemId: f.dictionaryItemId,
    name: f.name,
    productTypeId: f.productTypeId,
    productTypeName: PRODUCT_TYPE_NAMES[f.productTypeId] ?? f.name,
    costCheckState: f.costCheckState,
    mismatchingCostLine: f.mismatchingCostLine,
    pennyExactPct: f.pennyExactPct,
    within5PctPct: f.within5PctPct,
    lastCheckedAt: f.lastCheckedAt,
    rollingChecksCount: countRollingSetCells(f),
    quarantinedPriceCount: quarantinedPricesFor(f.dictionaryItemId).length,
    usedByCount: f.usedByCount,
    lastCheck: lastCheckForFixture(f),
    // Every fixture in this older cost-engine-validation mock predates the
    // fulfilment-route feature and models a made-in-house item only —
    // `mock-data/fulfilment-routes.ts` is the one that models a real
    // multi-route coverage picture, including supplier-only items.
    isMadeInHouse: true,
    routeCoverage: {
      dictionaryItemId: f.dictionaryItemId,
      availableRouteCount: 1,
      target: 3,
      routes: [
        {
          route: { code: "in_house", label: "In-house" },
          isAvailable: true,
          reason: null,
        },
      ],
    },
  };
}

const SORT_KEY_FNS: Record<string, (f: DictionaryValidationFixture) => string> =
  {
    name: (f) => f.name,
    product_type_id: (f) => String(f.productTypeId).padStart(6, "0"),
    cost_check_state: (f) => f.costCheckState,
  };

export function listMockDictionaryValidationItems(
  options: DictionaryValidationListOptions,
): ListResponse<DictionaryValidationIndexEntry> {
  let items = [...FIXTURES];

  if (options.q) {
    const needle = options.q.toLowerCase();
    items = items.filter((f) => f.name.toLowerCase().includes(needle));
  }
  if (options.productTypeId != null) {
    items = items.filter((f) => f.productTypeId === options.productTypeId);
  }
  if (options.costCheckState) {
    items = items.filter((f) => f.costCheckState === options.costCheckState);
  }

  const tokens = (options.sort ?? "name")
    .split(",")
    .map((t) => t.trim())
    .filter(Boolean);
  items = [...items].sort((a, b) =>
    a.dictionaryItemId.localeCompare(b.dictionaryItemId),
  );
  for (const token of [...tokens].reverse()) {
    const desc = token.startsWith("-");
    const fieldName = token.replace(/^[+-]/, "");
    const keyFn = SORT_KEY_FNS[fieldName];
    if (!keyFn) throw new Error(`unknown sort field: ${fieldName}`);
    items = [...items].sort((a, b) => {
      const ka = keyFn(a);
      const kb = keyFn(b);
      const cmp = ka < kb ? -1 : ka > kb ? 1 : 0;
      return desc ? -cmp : cmp;
    });
  }

  const page = options.page ?? 1;
  const pageSize = options.pageSize ?? 25;
  const total = items.length;
  const start = (page - 1) * pageSize;
  return {
    items: items.slice(start, start + pageSize).map(indexEntryFromFixture),
    total,
    page,
    pageSize,
    totalPages: total === 0 ? 0 : Math.ceil(total / pageSize),
  };
}

export function getMockDictionaryValidationFeedsStatus(): DictionaryValidationFeedsStatus {
  // Must equal the number of prices actually carrying a rolling-set origin
  // across every fixture — a header-card total that doesn't match the
  // rolling validation page's own "by item" view is exactly the kind of
  // cross-page inconsistency this fixture must not have. A previous
  // version padded this with an unmodelled "wider dictionary" constant to
  // force the header card's over-budget "N will wait" wording; that number
  // wasn't backed by any real row, so drop it — at this small a set size
  // the honest state is simply under budget.
  const rollingSetSize = FIXTURES.flatMap((f) =>
    f.variants.flatMap((v) => v.cells),
  ).filter((c) => c.origin != null).length;
  return {
    atEstimate: {
      feed: "at_estimate",
      healthy: true,
      lastRunAt: isoDaysAgo(0, 12, 4),
      countToday: 3,
      failuresToday: 0,
    },
    rollingValidation: {
      feed: "rolling_validation",
      healthy: true,
      lastRunAt: rollingSliceAt(-1), // this hour's slice
      mintsUsedThisWeek: 24,
      mintBudgetThisWeek: 30,
      // A scoped run an admin asked for from a terminal
      // (`cis cost-check run --item dict-brochure --budget 10`). Its mints are
      // part of the 24 above — the weekly allowance is one pot.
      scopedRunsThisWeek: [
        {
          runId: "run-scoped-1",
          scope: "item:dict-brochure-cover-text",
          startedAt: isoDaysAgo(1, 11, 0),
          mintsAttempted: 6,
          pricesChecked: 6,
          outcome: "completed",
        },
      ],
      nextScheduledAt: rollingSliceAt(0), // the next hourly slice
    },
    rollingSetSize,
    quarantinedPriceCount: [...quarantinedByItem.values()].flat().length,
    checkWindowWeeks: CHECK_WINDOW_WEEKS,
  };
}

/** All checks for a cost line — capped to 12 (the drill-in list length; the
 *  reported `checkCount` on the index still carries the real total). Every
 *  miss within that cap gets an explicit or evenly-split Δ, so the board's
 *  money summary and the drill-in list are always the same source — never a
 *  hand-maintained twin that can drift. */
function checksForCostLine(fixture: CostLineFixture): CostLineCheck[] {
  if (fixture.checkSeeds.length === 0) return [];
  const count = Math.min(fixture.totalCheckCount, 12);
  const aggregateDeltaPence =
    (fixture.ourMoneyPence ?? 0) - (fixture.tharsternMoneyPence ?? 0);
  const missDeltas =
    fixture.missDeltasPence ??
    Array.from(
      { length: fixture.missCount },
      () =>
        -Math.round(
          Math.abs(aggregateDeltaPence) / Math.max(fixture.missCount, 1),
        ),
    );
  const checks: CostLineCheck[] = [];
  for (let i = 0; i < count; i++) {
    const seed = fixture.checkSeeds[i % fixture.checkSeeds.length];
    const isMiss = i < fixture.missCount;
    const deltaPence = isMiss ? missDeltas[i] : 0;
    const isAtEstimate = i % 3 === 0;
    // Order now sets the estimate to ORDERED immediately, so it's always
    // "ordered"; a Send-estimate checkout only sometimes goes on to become
    // an order (every third at-estimate check here, for a realistic mix).
    const raisedBy = isAtEstimate
      ? i % 6 === 0
        ? "order_now"
        : "send_estimate"
      : null;
    const checkedAt = isoDaysAgo(i, 9 + (i % 6), 0);
    const orderedAt =
      raisedBy === "order_now"
        ? checkedAt
        : raisedBy === "send_estimate" && i % 9 === 0
          ? isoDaysAgo(Math.max(i - 1, 0), 10, 0)
          : null;
    const quantity = [250, 500, 1000][i % 3];
    checks.push({
      _self: costCheckPairSelf(seed.dictionaryItemId, seed.variantId, quantity),
      type: "cost_check",
      checkId: `check-${costLineFixtureKey(fixture.costLine)}-${i}`,
      feed: isAtEstimate ? "at_estimate" : "rolling_validation",
      checkedAt,
      productTypeId: seed.productTypeId,
      dictionaryItemId: seed.dictionaryItemId,
      dictionaryItemName: seed.dictionaryItemName,
      variantId: seed.variantId,
      variantLabel: seed.variantLabel,
      quantity,
      ourValuePence: 10000 + i * 137,
      tharsternValuePence: 10000 + i * 137 - deltaPence,
      deltaPence,
      result: isMiss ? fixture.health : "green",
      note: isMiss ? "Diverges from Tharstern's workings on this line" : null,
      raisedBy,
      orderedAt,
      // Before the estate unlocks, most checks are tharstern_recorded (price_spec
      // refused, so the ledger recorded
      // Tharstern's own route) — every third check is ours_forced instead,
      // so a fixture with 3+ checks shows both and a short one stays
      // honestly all-tharstern_recorded (spec 03 §4a, spec 07 §8.5/§10).
      routeSource: i % 3 === 2 ? "ours_forced" : "tharstern_recorded",
    });
  }
  return checks;
}

function summariseCostLineMoney(checks: CostLineCheck[]): CostLineMoneySummary {
  const differing = checks.filter((c) => c.deltaPence !== 0);
  const overs = differing.filter((c) => c.deltaPence > 0);
  const unders = differing.filter((c) => c.deltaPence < 0);
  return {
    differenceTotalPence: differing.reduce((sum, c) => sum + c.deltaPence, 0),
    differingCheckCount: differing.length,
    largestOverPence: overs.length
      ? Math.max(...overs.map((c) => c.deltaPence))
      : null,
    largestOverCount: overs.length,
    largestUnderPence: unders.length
      ? Math.min(...unders.map((c) => c.deltaPence))
      : null,
    largestUnderCount: unders.length,
  };
}

function affectedItemsFor(fixture: CostLineFixture): CostLineAffectedItem[] {
  const seen = new Map<string, CostLineAffectedItem>();
  for (const seed of fixture.checkSeeds) {
    if (!seen.has(seed.dictionaryItemId)) {
      seen.set(seed.dictionaryItemId, {
        dictionaryItemId: seed.dictionaryItemId,
        name: seed.dictionaryItemName,
        productTypeId: seed.productTypeId,
      });
    }
  }
  return [...seen.values()];
}

export function listMockValidationCostLines(): ListResponse<CostLineIndexEntry> {
  const items: CostLineIndexEntry[] = COST_LINE_FIXTURES.map((f) => ({
    _self: costLineSelf(f.costLine),
    type: "cost_line",
    checksUrl: costLineChecksUrl(f.costLine),
    costCentreUrl: costCentreUrlFor(f.costLine.costCentreId),
    costLine: f.costLine,
    health: f.health,
    productTypesAffected: f.productTypesAffected,
    checkCount: f.totalCheckCount,
    missCount: f.missCount,
    ourMoneyPence: f.ourMoneyPence,
    tharsternMoneyPence: f.tharsternMoneyPence,
    moneySummary: summariseCostLineMoney(checksForCostLine(f)),
    lastCheckedAt: f.lastCheckedAt,
    affectedDictionaryItems: affectedItemsFor(f),
    standardsRows: f.standardsRows,
  }));
  // Worst-first: mismatch, then small differences, then not yet checked,
  // then matches — the board's whole point is surfacing what needs attention.
  const severity: Record<CostCheckState, number> = {
    red: 0,
    amber: 1,
    grey: 2,
    green: 3,
  };
  items.sort((a, b) => severity[a.health] - severity[b.health]);
  return {
    items,
    total: items.length,
    page: 1,
    pageSize: items.length,
    totalPages: 1,
  };
}

export function getMockValidationCostLine(
  costCentreId: number,
  component: string,
): CostLineIndexEntry | null {
  const fixture = costLineFixturesByKey.get(`${costCentreId}:${component}`);
  if (!fixture) return null;
  return {
    _self: costLineSelf(fixture.costLine),
    type: "cost_line",
    checksUrl: costLineChecksUrl(fixture.costLine),
    costCentreUrl: costCentreUrlFor(fixture.costLine.costCentreId),
    costLine: fixture.costLine,
    health: fixture.health,
    productTypesAffected: fixture.productTypesAffected,
    checkCount: fixture.totalCheckCount,
    missCount: fixture.missCount,
    ourMoneyPence: fixture.ourMoneyPence,
    tharsternMoneyPence: fixture.tharsternMoneyPence,
    moneySummary: summariseCostLineMoney(checksForCostLine(fixture)),
    lastCheckedAt: fixture.lastCheckedAt,
    affectedDictionaryItems: affectedItemsFor(fixture),
    standardsRows: fixture.standardsRows,
  };
}

export function listMockCostLineChecks(
  costCentreId: number,
  component: string,
): CostLineCheck[] {
  const fixture = costLineFixturesByKey.get(`${costCentreId}:${component}`);
  if (!fixture) return [];
  return checksForCostLine(fixture);
}

export function getMockDictionaryLiveCostingDetail(
  dictionaryItemId: string,
): LiveCostingDetail {
  const fixture = fixturesById.get(dictionaryItemId);
  if (!fixture) {
    return {
      _self: dictionaryItemSelf(dictionaryItemId),
      type: "dictionary_item",
      dictionaryItemId,
      name: dictionaryItemId,
      productTypeId: 0,
      checklist: [],
      quarantinedPrices: [],
    };
  }
  return {
    _self: dictionaryItemSelf(fixture.dictionaryItemId),
    type: "dictionary_item",
    dictionaryItemId: fixture.dictionaryItemId,
    name: fixture.name,
    productTypeId: fixture.productTypeId,
    checklist: allChecksFor(fixture),
    quarantinedPrices: quarantinedPricesFor(fixture.dictionaryItemId),
  };
}

// ── Live costing, all items — Settings › Manufacturing › Live costing ────
// Estate-wide status over the SAME fixtures as the dictionary index above —
// never a second, parallel item-state model (see
// `manufacturing-setup-types.ts`'s `LiveCostingEstateDetail`).

export function getMockLiveCostingEstate(): LiveCostingEstateDetail {
  return {
    _self: "/views/live-costing/estate",
    type: "live_costing_estate",
    items: [...FIXTURES]
      .sort((a, b) => a.name.localeCompare(b.name))
      .map(indexEntryFromFixture),
  };
}

/** The checks the item's checklist lists, as `evaluate_live_costing_checks`
 *  decides it (decision 76, Ben 25 Sep): the approved four always, and
 *  weekly coverage only when it is the one thing failing. One helper, so the
 *  item page and the estate page can never classify a check differently. */
function allChecksFor(fixture: DictionaryValidationFixture) {
  const checks = checklistChecksFor(fixture);
  const isOnlyFailure = checks.every((c) =>
    c.key === "rolling_coverage" ? !c.met : c.met,
  );
  return checks.filter((c) => c.key !== "rolling_coverage" || isOnlyFailure);
}

/** The checks an item is held to — the four plus weekly coverage, as
 *  `cost_check_module`'s `evaluate_live_costing_checks` rules it. Computed
 *  from the fixture's own cells so it can never drift from the matrix. */
function checklistChecksFor(fixture: DictionaryValidationFixture) {
  const count = countRollingSetCells(fixture);
  return [
    ...fixture.checklist,
    {
      key: "rolling_coverage",
      met: count > 0,
      label:
        count > 0
          ? `${count} of this item's prices are checked weekly`
          : "None of this item's prices are checked weekly",
    },
  ];
}

/** The cost-check half of a matrix row: its variant and ledger cells. The
 *  route dimension — the served in-house cells and the supplier rows — is
 *  layered on by `mock-data/fulfilment-routes.ts`, which is what the mock
 *  adapter's matrix methods return, as the one real endpoint does. */
export type CostCheckMatrixRow = Omit<
  ValidationMatrixRow,
  "inHouse" | "supplierRoutes"
>;

/** A matrix before its route dimension (see `CostCheckMatrixRow`). */
export type CostCheckMatrix = Omit<
  DictionaryValidationMatrix,
  "rows" | "routes" | "isMadeInHouse"
> & { rows: CostCheckMatrixRow[] };

function matrixFromFixture(
  fixture: DictionaryValidationFixture,
  options: DictionaryValidationMatrixOptions = {},
): CostCheckMatrix {
  // A corner row sits straight after its own variant's plain row, as the
  // views endpoint lays them out.
  const plainFirst = [
    ...fixture.variants.filter((v) => !v.fixedCostOptions?.length),
  ].flatMap((plain) => [
    plain,
    ...fixture.variants.filter(
      (v) => v.fixedCostOptions?.length && v.variantId === plain.variantId,
    ),
  ]);
  const allRows: CostCheckMatrixRow[] = plainFirst.map((v) => ({
    variantId: v.variantId,
    displayLabel: v.displayLabel,
    isActive: v.isActive ?? true,
    fixedCostOptions: v.fixedCostOptions ?? [],
    cells: cellsFor(v),
  }));
  const allCells = allRows.flatMap((r) => r.cells);

  let filteredRows = allRows;
  if (options.variantQuery) {
    const needle = options.variantQuery.toLowerCase();
    filteredRows = filteredRows.filter((r) =>
      r.displayLabel.toLowerCase().includes(needle),
    );
  }
  if (options.costCheckState) {
    filteredRows = filteredRows.filter((r) =>
      r.cells.some((c) => c.state === options.costCheckState),
    );
  }
  const columns = [
    ...new Set(filteredRows.flatMap((r) => r.cells.map((c) => c.quantity))),
  ].sort((a, b) => a - b);
  const filteredRowCount = filteredRows.length;

  const rows =
    options.page != null || options.pageSize != null
      ? filteredRows.slice(
          ((options.page ?? 1) - 1) * (options.pageSize ?? 20),
          (options.page ?? 1) * (options.pageSize ?? 20),
        )
      : filteredRows;

  return {
    dictionaryItemId: fixture.dictionaryItemId,
    costedAt: COSTED_AT,
    validatedCellCount: allCells.filter((c) => c.state === "green").length,
    totalCellCount: allCells.length,
    columns,
    rows,
    filteredRowCount,
  };
}

export function getMockDictionaryValidationMatrix(
  dictionaryItemId: string,
  options?: DictionaryValidationMatrixOptions,
): CostCheckMatrix | null {
  const fixture = fixturesById.get(dictionaryItemId);
  return fixture ? matrixFromFixture(fixture, options) : null;
}

function findCell(
  fixture: DictionaryValidationFixture,
  variantId: string,
  quantity: number,
  fixedCostOptions: readonly FixedCostOptionChip[] = [],
): { variant: VariantSpec; cell: CellSpec } | null {
  const wanted = fixedCostOptionsKey(fixedCostOptions);
  const variant = fixture.variants.find(
    (v) =>
      v.variantId === variantId &&
      fixedCostOptionsKey(v.fixedCostOptions ?? []) === wanted,
  );
  if (!variant) return null;
  const cell = variant.cells.find((c) => c.quantity === quantity);
  if (!cell) return null;
  return { variant, cell };
}

/** "Check now" — queues a one-off mint, immediately re-classifies (mock has
 *  no real async engine run). A cell already matching stays matching; a
 *  mismatching or not-yet-checked cell resolves to matching (the point of
 *  asking for fresh evidence). */
export function requestMockCellCheck(
  dictionaryItemId: string,
  variantId: string,
  quantity: number,
  fixedCostOptions: readonly FixedCostOptionChip[] = [],
): ValidationMatrixCell {
  const fixture = fixturesById.get(dictionaryItemId);
  if (!fixture) throw new Error(`No dictionary item ${dictionaryItemId}`);
  const found = findCell(fixture, variantId, quantity, fixedCostOptions);
  if (!found) throw new Error(`No cell at ${variantId}/${quantity}`);
  // A substitution survives a re-check: Tharstern still swaps the stock
  // until the mapping is fixed by PR.
  if (found.cell.state !== "grey" && !found.cell.substitution) {
    found.cell.state = "green";
    found.cell.costLine = undefined;
    found.cell.reason = undefined;
  }
  found.cell.lastCheckedAt = new Date().toISOString();
  const [cell] = cellsFor({ ...found.variant, cells: [found.cell] });
  return cell;
}

/** The substitution the last check at this price found, if any. */
export function getMockCheckSubstitution(
  dictionaryItemId: string,
  variantId: string,
  quantity: number,
  fixedCostOptions: readonly FixedCostOptionChip[] = [],
): CheckSubstitution | null {
  const fixture = fixturesById.get(dictionaryItemId);
  const found = fixture
    ? findCell(fixture, variantId, quantity, fixedCostOptions)
    : undefined;
  return found?.cell.substitution ?? null;
}

/** Pin ("Keep checking this price") or unpin ("Stop checking this price") a
 *  single price into the rolling validation set — the admin's own action,
 *  which always wins over whatever the scheduler had previously picked.
 *  Rolling validation has no coarser item-level "include" flag. */
export function setMockCellRollingValidationPin(
  dictionaryItemId: string,
  variantId: string,
  quantity: number,
  pinned: boolean,
  fixedCostOptions: readonly FixedCostOptionChip[] = [],
): ValidationMatrixCell {
  const fixture = fixturesById.get(dictionaryItemId);
  if (!fixture) throw new Error(`No dictionary item ${dictionaryItemId}`);
  const found = findCell(fixture, variantId, quantity, fixedCostOptions);
  if (!found) throw new Error(`No cell at ${variantId}/${quantity}`);
  found.cell.origin = pinned ? "pinned" : undefined;
  found.cell.rollingSetAddedAt = pinned ? new Date().toISOString() : undefined;
  const [cell] = cellsFor({ ...found.variant, cells: [found.cell] });
  return cell;
}

/** Check history must always agree with the pair's own current Ours/
 *  Tharstern total (the same figures the summary strip and the line-item
 *  breakdown's "Total" row show) — otherwise the page tells two different
 *  stories about the same price. The latest entry carries the current
 *  state's real delta; earlier entries are shown as fully matching (a
 *  plausible history for a difference that only opened up recently). */
function checkHistoryFor(
  dictionaryItemId: string,
  cell: CellSpec,
  ourTotalPence: number | null,
  tharsternTotalPence: number | null,
): CostCheckHistoryEntry[] {
  if (cell.state === "grey" || ourTotalPence == null) return [];
  const result: CostCheckState =
    cell.state === "checking" ? "grey" : (cell.state as CostCheckState);
  const currentDeltaPence =
    tharsternTotalPence != null ? ourTotalPence - tharsternTotalPence : 0;
  const entries: CostCheckHistoryEntry[] = [];
  for (let i = 0; i < 4; i++) {
    const isLatest = i === 0;
    const deltaPence = isLatest ? currentDeltaPence : 0;
    const isAtEstimate = i % 2 === 0;
    // As above: Order now always shows "ordered"; Send estimate sometimes
    // hasn't been ordered yet.
    const raisedBy = isAtEstimate
      ? i % 4 === 0
        ? "order_now"
        : "send_estimate"
      : null;
    const checkedAt = isoDaysAgo(i * 7 + 1);
    const orderedAt = raisedBy === "order_now" ? checkedAt : null;
    entries.push({
      checkId: `hist-${dictionaryItemId}-${cell.quantity}-${i}`,
      feed: isAtEstimate ? "at_estimate" : "rolling_validation",
      checkedAt,
      result: isLatest ? result : "green",
      ourValuePence: ourTotalPence,
      tharsternValuePence: ourTotalPence - deltaPence,
      deltaPence,
      raisedBy,
      orderedAt,
      // A natural mix so both "On Tharstern's route" and "On our route"
      // render across a pair's own history (spec 07 §8.5/§10) — see
      // `checksForCostLine`'s own routeSource comment for the semantics.
      routeSource: i % 2 === 0 ? "tharstern_recorded" : "ours_forced",
    });
  }
  return entries;
}

// ── Generated pair breakdowns ─────────────────────────────────────────────
// Fallback for a checked pair with no hand-authored `lineTemplates`/
// `specTemplates` entry — every pair with an actual check must show real
// Calculation/Specification content, never "Not costed yet" alongside a
// priced Ours/Tharstern total (coordinator review round 5). Covers
// Greeting cards' 46 generated variants, the brochure's bct-2 and
// un-authored bct-1 quantities, and any future checked pair nobody got
// round to hand-authoring.
//
// Deterministic and honest: it never invents a press, a finishing step, or
// a total independently. The press(es) come from the fixture's own
// `pressesConsidered` (the ones actually "chosen"); the finishing cost
// centres come from the fixture's own `costCentreReasons` (minus the
// presses themselves and plate-making, which is always origination, never
// finishing) — established once by whoever hand-authored that fixture's
// first pair, reused here rather than re-derived. A product type with no
// step map yet synced (PT91 Greeting cards is marked `isReplicated: false`
// in `manufacturing-setup.ts`) legitimately generates no finishing line —
// that's the real data gap, not a generator bug.
//
// The difference is never smeared evenly (coordinator round 7): every line
// matches ours-to-Tharstern exactly except the cell's own driving cost line
// (`cell.costLine`), which carries the whole Δ. When the driving line isn't
// one the generator would otherwise produce (e.g. an Other-material Ink
// line on a press that only otherwise gets make-ready/paper/run lines), it
// is added explicitly — the generated pair always contains the cost line
// the matrix cell names. A round-robin weighted split assigns ours-side
// pence per line, with the last line absorbing the integer-pence remainder,
// so the lines always sum to the pair's own preset totals exactly.

const DIGITAL_PRESS_IDS = new Set([1117, 5627, 5729, 5830]);

function costCentreIdFromPressCode(pressCode: string): number {
  return Number(pressCode.split(" ")[0]);
}

function formatCount(n: number): string {
  return n.toLocaleString("en-GB");
}

/** Real finishing operations, keyed by cost centre id — the generator's
 *  only source of an op code / operation name / quantity driver / standards
 *  row for a finishing line, so a generated line is never a placeholder
 *  "FinishingRun" nor names its cost-centre code where an operation name
 *  belongs (Specification as costed lists operations, never codes). */
interface FinishingOpTemplate {
  opCode: string;
  operationName: string;
  quantityDriver: (quantity: number) => string;
  standardsRow: string;
  minutesFor: (quantity: number) => number;
}

const FINISHING_OP_TEMPLATES: Record<number, FinishingOpTemplate> = {
  801: {
    opCode: "GUILLOTINETRIM",
    operationName: "guillotine trim",
    quantityDriver: (q) => `${formatCount(q)} books, 3-side trim`,
    standardsRow: "Guillotine setup + run, 3-side trim band",
    minutesFor: (q) => Math.max(1, Math.round(q / 28)),
  },
  830: {
    opCode: "SADDLESTITCH",
    operationName: "saddle-stitch",
    quantityDriver: (q) => `${formatCount(q)} books, 2-stitch saddle`,
    standardsRow: "Saddle stitcher run-speed band × book count",
    minutesFor: (q) => Math.max(1, Math.round(q / 70)),
  },
  835: {
    opCode: "SECTIONFOLD",
    operationName: "section fold",
    quantityDriver: (q) => `${formatCount(q)} sections`,
    standardsRow: "KH82 fold run-speed band × section count",
    minutesFor: (q) => Math.max(1, Math.round(q / 14)),
  },
  5317: {
    opCode: "PERFECTBINDRUN",
    operationName: "perfect bind",
    quantityDriver: (q) => `${formatCount(q)} books, perfect bind`,
    standardsRow: "Horizon BQ470 perfect-bind run-speed band × book count",
    minutesFor: (q) => Math.max(1, Math.round(q / 18)),
  },
  814: {
    opCode: "LAMINATERUN",
    operationName: "gloss lamination",
    quantityDriver: (q) => `${formatCount(q)} sheets, gloss lamination`,
    standardsRow: "Laminator run-speed band × sheet count",
    minutesFor: (q) => Math.max(1, Math.round(q / 40)),
  },
};

function finishingOpTemplate(costCentreId: number): FinishingOpTemplate {
  return (
    FINISHING_OP_TEMPLATES[costCentreId] ?? {
      opCode: "FINISHINGRUN",
      operationName: ccName(costCentreId).toLowerCase(),
      quantityDriver: (q) => `${formatCount(q)} units`,
      standardsRow: "Finishing run-speed band × unit count",
      minutesFor: (q) => Math.max(1, Math.round(q / 30)),
    }
  );
}

/** One line the generator plans to emit, before pence are assigned. `role`
 *  identifies which naturally-generated line (if any) a cell's driving cost
 *  line matches onto; `matches` is that predicate. */
interface PlannedLine {
  costCentreId: number;
  costHead: CostHead;
  opCode: string;
  quantityDriver: string;
  standardsRow: string;
  ourTimeMinutes: number | null;
  weight: number;
  matchesCostLine: (line: CostLineRef) => boolean;
}

interface GeneratedBreakdown {
  lines: CostPairLine[];
  specification: SpecificationField[];
}

/** "250gsm silk" → "250 gsm silk" — every hand-authored spec row spaces the
 *  weight out from its unit; the generator matches that style rather than
 *  echoing the compact form the display label uses. */
function spaceOutUnit(s: string): string {
  return s.replace(/(\d)(gsm|microns?)\b/gi, "$1 $2");
}

/** Parses the two structured variant-label shapes the fixtures actually use
 *  into "Specification as costed" rows — never a single opaque label row
 *  when the label itself is structured. Returns `null` for a label that
 *  matches neither shape (e.g. Greeting cards' "A6, 300gsm silk"), which
 *  keeps the single-row fallback below. */
function parseVariantSpecificationRows(
  displayLabel: string,
): { label: string; value: string }[] | null {
  const coverText = displayLabel.match(
    /^Cover\s+([^+]+?)\s*\+\s*Text\s+([^,]+?),\s*([^+]+?)\s*\+\s*(.+)$/i,
  );
  if (coverText) {
    const [, coverStockRaw, textStockRaw, coverPages, textPages] = coverText;
    const coverStock = spaceOutUnit(coverStockRaw.trim());
    let textStock = spaceOutUnit(textStockRaw.trim());
    // Text often names only a weight ("130gsm") and inherits the cover's
    // material — the same reading the hand-authored specs make.
    if (/^\d+\s*gsm$/i.test(textStock)) {
      const material = coverStock.replace(/^[\d.]+\s*gsm\s*/i, "").trim();
      if (material) textStock = `${textStock} ${material}`;
    }
    return [
      { label: "Cover stock", value: coverStock },
      { label: "Cover pages", value: coverPages.trim() },
      { label: "Text stock", value: textStock },
      { label: "Text pages", value: textPages.trim() },
    ];
  }
  const stockFinish = displayLabel.match(/^([^,]+),\s*(.+)$/);
  if (stockFinish && /\d\s*(gsm|microns?)\b/i.test(stockFinish[1])) {
    return [
      { label: "Stock", value: spaceOutUnit(stockFinish[1].trim()) },
      { label: "Finish", value: stockFinish[2].trim() },
    ];
  }
  return null;
}

/** Finished size per product type, where the dictionary item's own spec
 *  fixes it (a brochure is always A4) rather than varying by variant like
 *  Greeting cards' per-variant sizes — included only "where known". */
const KNOWN_FINISHED_SIZE: Record<number, string> = {
  1: "A4",
};

function generatePairBreakdown(
  fixture: DictionaryValidationFixture,
  variant: VariantSpec,
  quantity: number,
  ourTotalPence: number,
  tharsternTotalPence: number,
  drivingCostLine: CostLineRef | null,
): GeneratedBreakdown {
  const chosenPresses = fixture.pressesConsidered.filter(
    (p) => p.outcome === "chosen",
  );
  const pressIds = chosenPresses.map((p) =>
    costCentreIdFromPressCode(p.pressCode),
  );
  // Plate-making is always origination, never finishing (it gets its own
  // line below, once per litho press) — a plate-making id in
  // `costCentreReasons` must never fall into this bucket just because it
  // isn't a press.
  const finishingIds = Object.keys(fixture.costCentreReasons)
    .map(Number)
    .filter(
      (id) =>
        !pressIds.includes(id) &&
        !isOriginationProcessType(getMockCostCentreRef(id).processTypeId),
    );

  const planned: PlannedLine[] = [];

  chosenPresses.forEach((press) => {
    const costCentreId = costCentreIdFromPressCode(press.pressCode);
    const isLitho = !DIGITAL_PRESS_IDS.has(costCentreId);
    const sheets = quantity;

    planned.push({
      costCentreId,
      costHead: "printing",
      opCode: isLitho ? "LITHOMR" : "DIGITALMR",
      quantityDriver: `1 make-ready, ${isLitho ? "litho" : "digital"}`,
      standardsRow: `${isLitho ? "Litho" : "Digital"} make-ready band, ${press.pressName}`,
      ourTimeMinutes: isLitho ? 5 : 3,
      weight: 0.05,
      matchesCostLine: () => false,
    });
    if (isLitho) {
      planned.push({
        costCentreId: 800,
        costHead: "origination",
        opCode: "PLATEMAKING",
        quantityDriver: "Plates, one per colour per section",
        standardsRow:
          "Plate cost × plate count, one plate per colour per section",
        ourTimeMinutes: null,
        weight: 0.05,
        matchesCostLine: () => false,
      });
    }
    planned.push({
      costCentreId,
      costHead: "paper",
      opCode: "STOCKISSUE",
      quantityDriver: `${formatCount(sheets)} sheets, ${press.pressName}`,
      standardsRow: "Stock issue × sheet count",
      ourTimeMinutes: null,
      weight: 0.15,
      matchesCostLine: () => false,
    });
    planned.push({
      costCentreId,
      costHead: "printing",
      opCode: isLitho ? "LITHOPRINTRUN" : "DIGITALPRINTRUN",
      quantityDriver: `${formatCount(sheets)} sheets (SRA1)`,
      standardsRow: `Run speed band × sheet count, ${press.pressName}`,
      ourTimeMinutes: Math.max(1, Math.round(sheets / (isLitho ? 55 : 65))),
      weight: 0.35,
      matchesCostLine: (line) =>
        line.costCentreId === costCentreId && line.component === "Print run",
    });
  });

  finishingIds.forEach((id) => {
    const tpl = finishingOpTemplate(id);
    planned.push({
      costCentreId: id,
      costHead: "finishing",
      opCode: tpl.opCode,
      quantityDriver: tpl.quantityDriver(quantity),
      standardsRow: tpl.standardsRow,
      ourTimeMinutes: tpl.minutesFor(quantity),
      weight: 0.15,
      matchesCostLine: (line) =>
        line.costCentreId === id && line.component === "Finishing run",
    });
  });

  // The cell's own driving cost line must appear in the generated pair even
  // when it isn't a line the generator would otherwise produce (e.g. an
  // Other-material Ink line on a press that only otherwise gets
  // make-ready/paper/run) — inject it explicitly rather than smearing its Δ
  // across unrelated lines.
  let drivingIndex = drivingCostLine
    ? planned.findIndex((p) => p.matchesCostLine(drivingCostLine))
    : -1;
  if (drivingCostLine && drivingIndex === -1) {
    // "Ink" (other_material) is the one case the fixtures actually use —
    // a book-block ink-weight line, not a share of make-ready/paper/run —
    // given a real ink-weight quantity driver rather than the generic
    // "N units" fallback below.
    const isInk = drivingCostLine.component === "Ink";
    planned.push({
      costCentreId: drivingCostLine.costCentreId,
      costHead: drivingCostLine.costHead,
      opCode: isInk
        ? "LITHOUVPROCESSBLACK"
        : drivingCostLine.component.toUpperCase().replace(/\s+/g, ""),
      quantityDriver: isInk
        ? `${(quantity * 0.0005414).toFixed(4)} kg process black ink`
        : `${quantity} units, ${drivingCostLine.component.toLowerCase()}`,
      standardsRow: isInk
        ? `Ink coverage × sheet area, litho UV 5-colour band`
        : `${drivingCostLine.component} band × unit count, ${drivingCostLine.costCentreName}`,
      ourTimeMinutes: null,
      weight: 0.35,
      matchesCostLine: (line) =>
        line.costCentreId === drivingCostLine.costCentreId &&
        line.component === drivingCostLine.component,
    });
    drivingIndex = planned.length - 1;
  }

  const hasDelta = ourTotalPence !== tharsternTotalPence;
  // No press, no finishing centre, nothing to hang the total on — fall
  // back to one honest "whole job" line rather than an empty breakdown for
  // a pair that does have a total.
  if (planned.length === 0) {
    planned.push({
      costCentreId: drivingCostLine?.costCentreId ?? 0,
      costHead: drivingCostLine?.costHead ?? "printing",
      opCode: "WHOLEJOB",
      quantityDriver: `${quantity} units`,
      standardsRow: "Whole-job band × unit count",
      ourTimeMinutes: null,
      weight: 1,
      matchesCostLine: () => true,
    });
    drivingIndex = 0;
  }

  const totalWeight = planned.reduce((sum, p) => sum + p.weight, 0);
  const lines: CostPairLine[] = [];
  let ourLeft = ourTotalPence;
  let tharsternLeft = tharsternTotalPence;
  planned.forEach((p, i) => {
    const isLast = i === planned.length - 1;
    const isDriving = hasDelta && i === drivingIndex;
    const ourValuePence = isLast
      ? ourLeft
      : Math.round(ourTotalPence * (p.weight / totalWeight));
    ourLeft -= ourValuePence;
    // Every line matches exactly except the driving line, which is
    // computed last (via the running `tharsternLeft` total) so the whole Δ
    // lands there rather than being smeared across every line's own
    // rounding — the driving line is never assigned by the same
    // proportional split as its ours-side value.
    let tharsternValuePence: number;
    if (isLast) {
      tharsternValuePence = tharsternLeft;
    } else if (isDriving) {
      tharsternValuePence =
        ourValuePence - (ourTotalPence - tharsternTotalPence);
    } else {
      tharsternValuePence = ourValuePence;
    }
    tharsternLeft -= tharsternValuePence;

    const tharsternTimeMinutes =
      p.ourTimeMinutes == null
        ? null
        : isDriving && ourValuePence > 0
          ? Math.round(
              (p.ourTimeMinutes * tharsternValuePence * 10) / ourValuePence,
            ) / 10
          : p.ourTimeMinutes;

    lines.push({
      costCentreId: p.costCentreId,
      costCentreCode: p.costCentreId === 0 ? "" : ccCode(p.costCentreId),
      costHead: p.costHead,
      opCode: p.opCode,
      quantityDriver: p.quantityDriver,
      ourTimeMinutes: p.ourTimeMinutes,
      tharsternTimeMinutes,
      ourValuePence,
      tharsternValuePence,
      deltaPence: ourValuePence - tharsternValuePence,
      standardsRow: p.standardsRow,
    });
  });

  const typeName = PRODUCT_TYPE_NAMES[fixture.productTypeId] ?? fixture.name;
  const finishedSize = KNOWN_FINISHED_SIZE[fixture.productTypeId];
  const specification: SpecificationField[] = [
    {
      label: "Product type",
      weSubmitted: productTypeLabel(fixture.productTypeId, typeName),
      tharsternCosted: productTypeLabel(fixture.productTypeId, typeName),
      differs: false,
    },
  ];
  if (finishedSize) {
    specification.push({
      label: "Finished size",
      weSubmitted: finishedSize,
      tharsternCosted: finishedSize,
      differs: false,
    });
  }
  specification.push({
    label: "Quantity",
    weSubmitted: String(quantity),
    tharsternCosted: String(quantity),
    differs: false,
  });
  const parsedRows = parseVariantSpecificationRows(variant.displayLabel);
  if (parsedRows) {
    for (const row of parsedRows) {
      specification.push({
        label: row.label,
        weSubmitted: row.value,
        tharsternCosted: row.value,
        differs: false,
      });
    }
  } else {
    specification.push({
      label: "Specification",
      weSubmitted: variant.displayLabel,
      tharsternCosted: variant.displayLabel,
      differs: false,
    });
  }
  if (finishingIds.length > 0) {
    // Operation names, never cost-centre codes — Henry reads "saddle-stitch,
    // guillotine trim", not "FIN-STITCH-01, FIN-GUILLOTINE".
    const names = finishingIds.map(
      (id) => finishingOpTemplate(id).operationName,
    );
    const text = names.join(", ");
    specification.push({
      label: "Finishing",
      weSubmitted: text.charAt(0).toUpperCase() + text.slice(1),
      tharsternCosted: text.charAt(0).toUpperCase() + text.slice(1),
      differs: false,
    });
  }

  return { lines, specification };
}

export function getMockEngineValidationCostPair(
  dictionaryItemId: string,
  variantId: string,
  quantity: number,
  fixedCostOptions: readonly FixedCostOptionChip[] = [],
): EngineValidationCostPair | null {
  const fixture = fixturesById.get(dictionaryItemId);
  if (!fixture) return null;
  const found = findCell(fixture, variantId, quantity, fixedCostOptions);
  if (!found) return null;
  // A corner's options are part of the price, so a corner never borrows the
  // plain pair's hand-authored breakdown.
  const optionsKey = fixedCostOptionsKey(fixedCostOptions);
  const key = optionsKey
    ? `${variantId}:${quantity}:${optionsKey}`
    : `${variantId}:${quantity}`;
  const cellTharsternPence =
    found.cell.state === "grey"
      ? null
      : (found.cell.tharsternPence ?? found.cell.costPence);
  // Only use a breakdown authored for this exact variant/quantity — falling
  // back to another quantity's lines (as this used to) shows that other
  // quantity's prices and state under this one's heading, which is worse
  // than showing no breakdown at all. A checked pair (state != grey) with
  // no hand-authored lines gets a deterministic generated breakdown instead
  // (coordinator review round 5) — every checked pair shows real lines,
  // never "Not costed yet" alongside a priced total.
  const generated =
    fixture.lineTemplates[key] === undefined &&
    found.cell.state !== "grey" &&
    found.cell.costPence != null &&
    cellTharsternPence != null
      ? generatePairBreakdown(
          fixture,
          found.variant,
          quantity,
          found.cell.costPence,
          cellTharsternPence,
          found.cell.costLine ?? null,
        )
      : null;
  const lines = fixture.lineTemplates[key] ?? generated?.lines ?? [];
  const ourTotalPence =
    lines.length > 0
      ? lines.reduce((sum, l) => sum + l.ourValuePence, 0)
      : found.cell.state === "grey"
        ? null
        : found.cell.costPence;
  const tharsternTotalPence =
    lines.length > 0
      ? lines.reduce((sum, l) => sum + l.tharsternValuePence, 0)
      : cellTharsternPence;
  // "Specification as costed" — authored per (variant, quantity) in
  // `specTemplates`, never derived from the Calculation table's quantity
  // drivers (build decision, coordinator review round 2): product type,
  // finished size, quantity, then per-part stock/colours/pages, then
  // finishing. Falls back to the generated specification alongside
  // generated lines above; `null` only when the pair is genuinely not yet
  // checked (mirrors `lineTemplates`'s own "only this exact pair" rule).
  const specification: SpecificationField[] | null =
    fixture.specTemplates[key] ?? generated?.specification ?? null;
  return {
    _self: costCheckPairSelf(
      dictionaryItemId,
      variantId,
      quantity,
      found.variant.fixedCostOptions ?? [],
    ),
    type: "variant_quantity_price",
    checkNowUrl: CHECK_NOW_URL,
    rollingPinUrl: ROLLING_PIN_URL,
    dictionaryItemId,
    dictionaryItemName: fixture.name,
    variantId,
    variantLabel: found.variant.displayLabel,
    quantity,
    fixedCostOptions: found.variant.fixedCostOptions ?? [],
    costedAt: COSTED_AT,
    state: found.cell.state,
    costLine: found.cell.costLine ?? null,
    reason: found.cell.reason ?? null,
    substitution: found.cell.substitution ?? null,
    ourTotalPence,
    tharsternTotalPence,
    lines,
    costCentreReasons: fixture.costCentreReasons,
    pressesConsidered: fixture.pressesConsidered,
    specification,
    checkHistory: checkHistoryFor(
      dictionaryItemId,
      found.cell,
      ourTotalPence,
      tharsternTotalPence,
    ),
  };
}

// ── Rolling validation page's roll-up views ───────────────────────────────
// "By cost centre" and "by product type" over the same check ledger as "by
// item"/"by cost line" (`COST_LINE_FIXTURES`) — decision 12 Sep,
// `03_manufacturing_surfaces_design.md` "setup visibility and costing
// validation are separate". Curating rolling-set membership itself moved to
// the item matrix's Check now popover (`setMockCellRollingValidationPin`);
// there is no longer a standalone flat list of set members.

/** Site per cost centre — matches `mock-data/manufacturing-setup.ts`'s own
 *  cost-centre snapshot exactly so the two pages never disagree about where
 *  a press sits (same "Read it as Henry" consistency rule as the cost-line
 *  registry above). */
const COST_CENTRE_SITE: Record<number, string> = {
  5351: "EXETER01",
  803: "EXETER01",
  5830: "BUCKINGHAM01",
  1117: "EXETER01",
  5627: "BUCKINGHAM01",
  801: "EXETER01",
  835: "EXETER01",
  5607: "BUCKINGHAM01",
  5609: "BUCKINGHAM01",
};

/** One row per cost CENTRE, not per cost line — a cost centre can carry
 *  more than one component (5351 runs both an Ink material line and its
 *  own Print run line), and the by-cost-centre view groups them, summing
 *  each state's count across every line on that centre (coordinator
 *  review round 6; regression-tested by
 *  `cost-engine-validation.test.ts`'s "groups by cost centre" case). */
export function listMockValidationByCostCentre(): ListResponse<CostCentreValidationIndexEntry> {
  const byCostCentre = new Map<number, CostLineFixture[]>();
  for (const fixture of COST_LINE_FIXTURES) {
    const id = fixture.costLine.costCentreId;
    const list = byCostCentre.get(id) ?? [];
    list.push(fixture);
    byCostCentre.set(id, list);
  }
  const items: CostCentreValidationIndexEntry[] = [
    ...byCostCentre.entries(),
  ].map(([costCentreId, lines]) => {
    const first = lines[0].costLine;
    const lastCheckedDates = lines
      .map((l) => l.lastCheckedAt)
      .filter((d): d is string => d != null)
      .sort();
    return {
      _self: costCentreSetupUrl(costCentreId),
      type: "cost_centre",
      costCentreId,
      costCentreCode: first.costCentreCode,
      costCentreName: first.costCentreName,
      site: COST_CENTRE_SITE[costCentreId] ?? "EXETER01",
      priceChecksUsedIn: lines.reduce((sum, l) => sum + l.totalCheckCount, 0),
      lastCheckedAt: lastCheckedDates[lastCheckedDates.length - 1] ?? null,
      matchesCount: lines.filter((l) => l.health === "green").length,
      smallDifferencesCount: lines.filter((l) => l.health === "amber").length,
      mismatchCount: lines.filter((l) => l.health === "red").length,
    };
  });
  items.sort((a, b) => a.costCentreId - b.costCentreId);
  return {
    items,
    total: items.length,
    page: 1,
    pageSize: items.length,
    totalPages: 1,
  };
}

export function listMockValidationByProductType(): ListResponse<ProductTypeValidationIndexEntry> {
  const byProductType = new Map<number, DictionaryValidationFixture[]>();
  for (const fixture of FIXTURES) {
    const list = byProductType.get(fixture.productTypeId) ?? [];
    list.push(fixture);
    byProductType.set(fixture.productTypeId, list);
  }
  const items: ProductTypeValidationIndexEntry[] = [...byProductType.entries()]
    .map(([productTypeId, fixtures]) => {
      const allCells = fixtures.flatMap((f) =>
        f.variants.flatMap((v) => v.cells),
      );
      const checked = allCells.filter((c) => c.state !== "grey");
      const checkedDates = [...checked]
        .map((c) =>
          c.lastCheckedAt !== undefined ? c.lastCheckedAt : isoDaysAgo(2),
        )
        .filter((d): d is string => d != null)
        .sort();
      const lastCheckedAt = checkedDates[checkedDates.length - 1] ?? null;
      return {
        _self: `/views/manufacturing/product-types/${productTypeId}`,
        type: "product_type",
        productTypeId,
        productTypeName: fixtures[0].name,
        itemCount: fixtures.length,
        pricesCheckedInWindow: checked.length,
        matchesCount: allCells.filter((c) => c.state === "green").length,
        smallDifferencesCount: allCells.filter((c) => c.state === "amber")
          .length,
        mismatchCount: allCells.filter((c) => c.state === "red").length,
        notYetCheckedCount: allCells.filter((c) => c.state === "grey").length,
        lastCheckedAt,
      };
    })
    .sort((a, b) => a.productTypeId - b.productTypeId);
  return {
    items,
    total: items.length,
    page: 1,
    pageSize: items.length,
    totalPages: 1,
  };
}

// ── Check settings ───────────────────────────────────────────────────────

let checkSettings: CheckSettings = {
  matchesTolerancePct: 1,
  mismatchLimitPct: 10,
  sameDirectionCount: 3,
  sameDirectionWindow: 5,
  checkWindowWeeks: CHECK_WINDOW_WEEKS,
  weeklyCheckBudget: 30,
  // cost-engine-productionisation spec 03 §4 defaults; spec 07 places these
  // on the Check settings panel (an "Order guard" group + the control
  // budget beside the weekly one - see 00_build_plan.md §07 P-1).
  orderGuardPct: 10,
  orderGuardFloorPence: 500,
  controlMintBudgetPerWeek: 2,
  // The line floor (product owner, 19 Sep 2026): a line differing by less
  // than this many pence never trips a check.
  lineFloorPence: 10,
  // Rolling-run pacing (Ben, 24 Sep 2026): one mint a minute, 60 a run.
  mintIntervalSeconds: 60,
  runMintCap: 60,
  // Back-off after a Tharstern timeout (Ben, 24 Sep 2026): ten minutes.
  timeoutBackoffSeconds: 600,
  lastChangedBy: "Ben Pollard",
  lastChangedAt: isoDaysAgo(14, 10, 0),
  lastChangedNote: "Initial parameters, matching the accuracy model in doc 63.",
};

export function getMockCheckSettings(): CheckSettings {
  return checkSettings;
}

export function updateMockCheckSettings(
  request: CheckSettingsUpdateRequest,
  changedBy: string,
): CheckSettings {
  if (!request.note.trim()) {
    throw new Error("A note is required to change the check settings.");
  }
  checkSettings = {
    matchesTolerancePct: request.matchesTolerancePct,
    mismatchLimitPct: request.mismatchLimitPct,
    sameDirectionCount: request.sameDirectionCount,
    sameDirectionWindow: request.sameDirectionWindow,
    checkWindowWeeks: request.checkWindowWeeks,
    weeklyCheckBudget: request.weeklyCheckBudget,
    orderGuardPct: request.orderGuardPct,
    orderGuardFloorPence: request.orderGuardFloorPence,
    controlMintBudgetPerWeek: request.controlMintBudgetPerWeek,
    lineFloorPence: request.lineFloorPence,
    mintIntervalSeconds: request.mintIntervalSeconds,
    runMintCap: request.runMintCap,
    timeoutBackoffSeconds: request.timeoutBackoffSeconds,
    lastChangedBy: changedBy,
    lastChangedAt: new Date().toISOString(),
    lastChangedNote: request.note,
  };
  return checkSettings;
}

// ── Supersede checks ────────────────────────────────────────────────────────
//
// The mock keeps a small per-scope count of "checks an admin could still
// supersede", so the panel's preview → confirm → audit-list loop behaves the
// way it does against the real ledger: a count that drops to zero once the
// batch is superseded, and an audit row appended each time.

let mockSupersedableChecks: Record<string, number> = {
  all: 255,
  "product_type:33": 82,
  "dictionary_item:dict-perfect-bound-book": 14,
  "cost_centre:5351": 41,
};

let mockSupersedeEvents: SupersedeEvent[] = [
  {
    id: "evt-mock-1",
    scopeKind: "product_type",
    scopeValue: "36",
    checkedBefore: isoDaysAgo(2, 9, 0),
    reason: "engine build 122873eb4 deployed — bound-product sizing fix",
    supersededBy: "Ben Pollard",
    supersededAt: isoDaysAgo(2, 9, 5),
    checksSuperseded: 37,
    itemsAffected: 6,
  },
];

function supersedeScopeKey(request: SupersedeChecksRequest): string {
  return request.scopeKind === "all"
    ? "all"
    : `${request.scopeKind}:${request.scopeValue ?? ""}`;
}

/** Mirrors the action's own validation so the mock rejects exactly what the
 *  API rejects (cost_check_module `supersede_cost_checks._validate`). */
function assertSupersedeRequestValid(request: SupersedeChecksRequest): void {
  if (!request.reason.trim()) {
    throw new Error("A reason is required to supersede checks.");
  }
  if (request.scopeKind === "all" && request.scopeValue != null) {
    throw new Error("scopeValue must be omitted when scopeKind is 'all'.");
  }
  if (request.scopeKind !== "all" && !request.scopeValue?.trim()) {
    throw new Error(
      `scopeValue is required when scopeKind is '${request.scopeKind}'.`,
    );
  }
}

export function previewMockSupersedeChecks(
  request: SupersedeChecksRequest,
): SupersedePreview {
  assertSupersedeRequestValid(request);
  const checkCount = mockSupersedableChecks[supersedeScopeKey(request)] ?? 0;
  // Roughly six checks per item in the fixtures, and never more items than
  // checks — the panel only ever renders these as "N checks on M items".
  return {
    checkCount,
    itemCount: Math.min(checkCount, Math.ceil(checkCount / 6)),
  };
}

export function supersedeMockChecks(
  request: SupersedeChecksRequest,
  supersededBy: string,
): SupersedeChecksResult {
  const preview = previewMockSupersedeChecks(request);
  const key = supersedeScopeKey(request);
  mockSupersedableChecks = { ...mockSupersedableChecks, [key]: 0 };
  if (request.scopeKind === "all") {
    mockSupersedableChecks = Object.fromEntries(
      Object.keys(mockSupersedableChecks).map((k) => [k, 0]),
    );
  }
  const now = new Date().toISOString();
  const event: SupersedeEvent = {
    id: `evt-mock-${mockSupersedeEvents.length + 1}`,
    scopeKind: request.scopeKind,
    scopeValue: request.scopeValue ?? null,
    checkedBefore: request.checkedBefore ?? now,
    reason: request.reason.trim(),
    supersededBy,
    supersededAt: now,
    checksSuperseded: preview.checkCount,
    itemsAffected: preview.itemCount,
    // The mock has no background task queue, so the re-evaluation follow-on
    // is simulated as already finished -- the real API leaves these null
    // until its background task completes; the panel doesn't poll for them.
    reEvaluatedAt: now,
    itemsReEvaluated: preview.itemCount,
    itemsAutoPaused: 0,
  };
  mockSupersedeEvents = [event, ...mockSupersedeEvents];
  return {
    event,
    itemsQueuedForReEvaluation: preview.itemCount,
  };
}

export function listMockSupersedeEvents(): SupersedeEvent[] {
  return mockSupersedeEvents;
}
